import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Users,
  Globe,
  Shield,
  Plus,
  Trash2,
  Check,
  X,
  UserCheck,
  UserX,
  Link2,
  BarChart3,
  RefreshCw,
  Search,
  ChevronDown,
  MessageSquare,
  Bell,
  Send,
  Route,
  Edit2,
  Save,
  CreditCard,
  Bitcoin,
  Clock,
  CheckCircle,
  XCircle,
  ChevronRight,
  Key,
  Activity,
  Server,
  Wifi,
  WifiOff,
  Copy,
  Eye,
  EyeOff,
  FolderOpen,
  Tags,
  Star,
  FileText,
  AlertTriangle,
  Zap,
  Download,
  Upload,
  MousePointer,
  Lock,
  LogIn,
  Pause,
  Play
} from "lucide-react";
import ConfigSyncPanel from "../components/admin/ConfigSyncPanel";
import { WalletEditor } from "../components/admin/WalletEditor";
import { ServerFleetHeader } from "../components/admin/servers/ServerFleetHeader";
import { FleetParityPanel } from "../components/admin/servers/FleetParityPanel";
import { ServerCreatePanel } from "../components/admin/servers/ServerCreatePanel";
import { ServerCard } from "../components/admin/servers/ServerCard";
import { DeploymentCredentialsPanel } from "../components/admin/DeploymentCredentialsPanel";
import { DomainAssignmentWorkspace } from "../components/admin/domains/DomainAssignmentWorkspace";
import { UserManagementWorkspace } from "../components/admin/users/UserManagementWorkspace";
import { DomainPoolWorkspace } from "../components/admin/domains/DomainPoolWorkspace";
import { AdminSecondarySidebar } from "../components/admin/AdminSecondarySidebar";
import { AdminOverviewWorkspace } from "../components/admin/AdminOverviewWorkspace";
import { TransactionLedgerWorkspace } from "../components/admin/TransactionLedgerWorkspace";
import { SupportTicketWorkspace } from "../components/admin/SupportTicketWorkspace";
import { BroadcastWorkspace } from "../components/admin/BroadcastWorkspace";
import { LicenseControlWorkspace } from "../components/admin/LicenseControlWorkspace";
import { AdminSecurityWorkspace } from "../components/admin/AdminSecurityWorkspace";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const asArray = (value) => Array.isArray(value) ? value : [];
const asObject = (value, fallback = {}) => value && typeof value === "object" && !Array.isArray(value) ? value : fallback;

// Navigation items for left sidebar
const NAV_ITEMS = [
  { id: "overview", label: "Overview", icon: Activity, group: "operations" },
  { id: "users", label: "User Management", icon: Users, group: "operations" },
  { id: "licenses", label: "License Control", icon: Key, group: "operations" },
  { id: "transactions", label: "Transactions", icon: CreditCard, group: "operations" },
  { id: "support", label: "Support Tickets", icon: MessageSquare, group: "operations" },
  { id: "servers", label: "Redirect Servers", icon: Server, group: "infrastructure" },
  { id: "config-sync", label: "Config Sync", icon: RefreshCw, group: "infrastructure" },
  { id: "deployment", label: "Deployment Wizard", icon: Zap, group: "infrastructure" },
  { id: "domains", label: "Domain Pool", icon: Globe, group: "infrastructure" },
  { id: "assignments", label: "Domain Assignments", icon: Link2, group: "infrastructure" },
  { id: "alias-paths", label: "Alias Paths", icon: Route, group: "controls" },
  { id: "broadcast", label: "Broadcast", icon: Bell, group: "controls" },
  { id: "admin-security", label: "Admin Security", icon: Lock, group: "controls" }
];

const SuperAdmin = () => {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [adminNavSearch, setAdminNavSearch] = useState("");
  
  // Stats
  const [stats, setStats] = useState(null);
  
  // Users
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [pendingApprovalSearch, setPendingApprovalSearch] = useState("");
  
  // Domains
  const [domains, setDomains] = useState([]);
  const [domainGroups, setDomainGroups] = useState([]);
  const [newDomain, setNewDomain] = useState("");
  const [newDomainDesc, setNewDomainDesc] = useState("");
  const [newDomainGroup, setNewDomainGroup] = useState("");
  const [newDomainNotes, setNewDomainNotes] = useState("");
  const [newDomainRating, setNewDomainRating] = useState("average");
  const [newDomainTags, setNewDomainTags] = useState("");
  const [editingDomain, setEditingDomain] = useState(null);
  const [domainFilter, setDomainFilter] = useState("all");
  const [domainSearch, setDomainSearch] = useState("");
  const [quickAssignDomain, setQuickAssignDomain] = useState(null);
  const [quickAssignUser, setQuickAssignUser] = useState("");
  
  // Domain Groups
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupDesc, setNewGroupDesc] = useState("");
  const [newGroupColor, setNewGroupColor] = useState("#22d3ee");
  const [showGroupForm, setShowGroupForm] = useState(false);
  
  // Assignments
  const [assignments, setAssignments] = useState([]);
  const [assignmentSearch, setAssignmentSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedDomain, setSelectedDomain] = useState("");
  const [selectedGroupForAssign, setSelectedGroupForAssign] = useState("");
  const [assignCount, setAssignCount] = useState(3);
  const [assignMode, setAssignMode] = useState("single"); // "single" or "group"
  
  // Support Tickets
  const [tickets, setTickets] = useState([]);
  const [ticketResponse, setTicketResponse] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  
  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [newNotification, setNewNotification] = useState({ title: "", message: "", type: "info" });
  
  // Alias Paths
  const [aliasPaths, setAliasPaths] = useState([]);
  const [newAliasPath, setNewAliasPath] = useState("");
  const [editingPath, setEditingPath] = useState(null);
  const [editPathValue, setEditPathValue] = useState("");
  
  // System Path Generator
  const [genCount, setGenCount] = useState(10);
  const [genLoading, setGenLoading] = useState(false);
  const [generatedPaths, setGeneratedPaths] = useState([]);
  const [selectedGenPaths, setSelectedGenPaths] = useState(new Set());

  // Alias Path Bulk Selection
  const [selectedAliasPaths, setSelectedAliasPaths] = useState(new Set());
  
  // Alias Path Groups (Enhanced)
  const [pathGroups, setPathGroups] = useState([]);
  const [newPathGroup, setNewPathGroup] = useState({ name: "", description: "", color: "#f97316" });
  const [showPathGroupForm, setShowPathGroupForm] = useState(false);
  const [selectedPathGroup, setSelectedPathGroup] = useState(null);
  const [newPathForGroup, setNewPathForGroup] = useState("");
  const [licensedUsersForAssign, setLicensedUsersForAssign] = useState([]);
  const [selectedUsersForAssign, setSelectedUsersForAssign] = useState([]);
  const [showUserAssignModal, setShowUserAssignModal] = useState(false);
  const [assigningGroupId, setAssigningGroupId] = useState(null);
  const [groupUsers, setGroupUsers] = useState([]);
  const [showGroupUsersModal, setShowGroupUsersModal] = useState(false);
  const [viewingGroupId, setViewingGroupId] = useState(null);
  
  // Pending Transactions
  const [pendingTransactions, setPendingTransactions] = useState([]);
  const [walletSettings, setWalletSettings] = useState({ btc: "", usdt: "" });
  const [walletSaving, setWalletSaving] = useState(false);
  const [walletMessage, setWalletMessage] = useState("");
  const [transactionFilter, setTransactionFilter] = useState("pending");

  // Maintenance Mode
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("System is under maintenance. Please try again later.");
  const [updatingMaintenance, setUpdatingMaintenance] = useState(false);

  // Admin 2FA
  const [admin2FAEnabled, setAdmin2FAEnabled] = useState(false);
  const [admin2FASetup, setAdmin2FASetup] = useState(null);
  const [admin2FACode, setAdmin2FACode] = useState("");
  const [admin2FALoading, setAdmin2FALoading] = useState(false);
  const [admin2FAError, setAdmin2FAError] = useState("");

  // Redirect Servers
  const [servers, setServers] = useState([]);
  const [serverHealth, setServerHealth] = useState({}); // { [serverId]: {status, latency_ms} }
  const [checkingHealth, setCheckingHealth] = useState(null); // serverId currently probing (or "all")
  const [newServer, setNewServer] = useState({ name: "", ip_address: "", description: "" });
  const [showNewApiKey, setShowNewApiKey] = useState(null);
  const [lastGeneratedKey, setLastGeneratedKey] = useState(null); // Keep track of last generated key
  const [selectedServer, setSelectedServer] = useState(null);
  const [serverDomainAssign, setServerDomainAssign] = useState({ server_id: "", domain_id: "" });
  const [testingConnection, setTestingConnection] = useState(null);
  const [connectionResult, setConnectionResult] = useState(null);
  const [serverStats, setServerStats] = useState(null);
  const [showServerStats, setShowServerStats] = useState(null);
  const [setupDomainsServer, setSetupDomainsServer] = useState(null);
  const [setupSshPassword, setSetupSshPassword] = useState("");
  const [setupDomainsLoading, setSetupDomainsLoading] = useState(false);
  const [setupDomainsResult, setSetupDomainsResult] = useState(null);
  const [newDomainInput, setNewDomainInput] = useState("");
  const [rebalanceModal, setRebalanceModal] = useState({ open: false, source: null, plan: null, domainId: "", targetId: "", result: null, error: "" });
  const [rebalanceLoading, setRebalanceLoading] = useState(false);
  const [fingerproxySyncing, setFingerproxySyncing] = useState(null);
  const [fingerproxyResult, setFingerproxyResult] = useState(null);
  const [fleetParity, setFleetParity] = useState(null);
  const [fleetParityLoading, setFleetParityLoading] = useState(false);
  const [fleetParityRunning, setFleetParityRunning] = useState(false);
  const [fleetParityError, setFleetParityError] = useState("");

  // Confirmation Modal (replaces window.confirm which can be blocked by browsers)
  const [confirmModal, setConfirmModal] = useState({ show: false, title: "", message: "", onConfirm: null, onCancel: null });
  
  // Extend Subscription Modal
  const [extendModal, setExtendModal] = useState({ show: false, user: null });
  const [extendDays, setExtendDays] = useState(14);
  const [extendLoading, setExtendLoading] = useState(false);
  const [passwordResetModal, setPasswordResetModal] = useState({ show: false, user: null });
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);
  const [passwordResetError, setPasswordResetError] = useState("");

  // Cleanup Expired Users
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);

  const showConfirm = (title, message) => {
    return new Promise((resolve) => {
      setConfirmModal({
        show: true, title, message,
        onConfirm: () => { setConfirmModal({ show: false, title: "", message: "", onConfirm: null, onCancel: null }); resolve(true); },
        onCancel: () => { setConfirmModal({ show: false, title: "", message: "", onConfirm: null, onCancel: null }); resolve(false); }
      });
    });
  };

  // Pending Trials
  const [pendingTrials, setPendingTrials] = useState([]);
  const [trialHours, setTrialHours] = useState(3);
  const [grantingTrial, setGrantingTrial] = useState(null);

  // Deployment Wizard
  const [deploymentForm, setDeploymentForm] = useState({
    server_name: "",
    server_ip: "",
    domains: "",
    dashboard_url: "https://wizard-x-routes.shop/api",
    user_id: "",
    landing_page: "cloudflare",
    redirect_url: "",
    enable_telegram: false,
    telegram_bot_token: "",
    telegram_chat_id: "",
    register_in_pool: false, // opt-in pool registration (off by default)
    github_repo_url: "", // PAT-injected by backend from DEPLOY_GITHUB_REPO_URL env (admin can override with plain https://github.com/owner/repo.git)
    maxmind_account_id: "",
    maxmind_license_key: ""
  });
  const [generatedScript, setGeneratedScript] = useState(null);
  const [generatedApiKey, setGeneratedApiKey] = useState(null);
  const [generatedAgentAdmin, setGeneratedAgentAdmin] = useState(null);
  const [generatedServerId, setGeneratedServerId] = useState(null);
  const [landingPageTemplates, setLandingPageTemplates] = useState([]);
  const [generatingScript, setGeneratingScript] = useState(false);
  const [deploymentStep, setDeploymentStep] = useState(1); // 1: Form, 2: Script, 3: Instructions, 4: Deploying, 5: Done

  // Remote deployment state
  const [sshCredentials, setSshCredentials] = useState({
    ssh_user: "root",
    ssh_password: "",
    ssh_port: 22
  });
  const [deploymentLogs, setDeploymentLogs] = useState([]);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deploymentStatus, setDeploymentStatus] = useState(null); // null, "testing", "deploying", "copying", "success", "failed"
  const [connectionTested, setConnectionTested] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  // Next-Gen Bot Defense global defaults (admin)
  const [defenseDefaults, setDefenseDefaults] = useState({});
  const [defenseApplying, setDefenseApplying] = useState(false);
  const [defenseMsg, setDefenseMsg] = useState("");
  const [defenseOpen, setDefenseOpen] = useState(false);

  const DEFENSE_ITEMS = [
    { key: "enable_ja3", label: "TLS JA3/JA4" },
    { key: "enable_header_checks", label: "Header Checks" },
    { key: "enable_behavioral", label: "Behavioral Gate" },
    { key: "enable_crawler_cidr", label: "Crawler IP Block" },
    { key: "enable_fp_velocity", label: "FP Velocity" },
    { key: "enable_geo_consistency", label: "Geo Consistency" },
    { key: "second_hop_cloak", label: "Second-Hop Cloak" },
  ];

  const loadDefenseDefaults = async () => {
    try {
      const res = await axios.get(`${API}/admin/security-defaults`, { headers });
      setDefenseDefaults(asObject(res.data));
    } catch (e) { /* noop */ }
  };

  const applyDefenseDefaults = async (target) => {
    setDefenseApplying(true);
    setDefenseMsg("");
    try {
      const toggles = {};
      DEFENSE_ITEMS.forEach(i => { toggles[i.key] = !!defenseDefaults[i.key]; });
      const res = await axios.post(`${API}/admin/security-defaults/apply`, { toggles, target }, { headers });
      setDefenseMsg(`Applied to ${res.data.updated} ${target} user(s).`);
    } catch (e) {
      setDefenseMsg(e.response?.data?.detail || "Failed to apply defaults");
    }
    setDefenseApplying(false);
  };

  // Live Defense Stats + JA3 auto-learn review
  const [defenseStats, setDefenseStats] = useState(null);
  const [ja3Candidates, setJa3Candidates] = useState([]);
  const [ja3Reviewing, setJa3Reviewing] = useState(null);

  const fetchDefenseStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/defense-stats`, { headers });
      setDefenseStats(asObject(res.data));
    } catch (e) { /* noop */ }
  };

  const fetchJa3Candidates = async () => {
    try {
      const res = await axios.get(`${API}/admin/ja3-candidates?status=pending`, { headers });
      setJa3Candidates(res.data.candidates || []);
    } catch (e) { /* noop */ }
  };

  const reviewJa3 = async (ja3, action) => {
    setJa3Reviewing(ja3);
    try {
      await axios.post(`${API}/admin/ja3-candidates/review`, { ja3, action }, { headers });
      setJa3Candidates(prev => prev.filter(c => c.ja3 !== ja3));
      fetchDefenseStats();
    } catch (e) {
      alert(e.response?.data?.detail || "Review failed");
    }
    setJa3Reviewing(null);
  };


  const fetchPendingTrials = async () => {
    try {
      const res = await axios.get(`${API}/admin/trial/pending`, { headers });
      setPendingTrials(asArray(res.data?.pending_users));
    } catch (err) {
      console.error("Error fetching pending trials:", err);
    }
  };

  const grantTrial = async (userId, hours = 8) => {
    setGrantingTrial(userId);
    try {
      await axios.post(`${API}/admin/trial/grant`, { user_id: userId, hours }, { headers });
      await fetchPendingTrials();
      await fetchUsers();
      await fetchStats();
    } catch (err) {
      console.error("Error granting trial:", err);
      alert(err.response?.data?.detail || "Failed to grant trial");
    }
    setGrantingTrial(null);
  };

  const terminateTrial = async (userId) => {
    if (!await showConfirm("Terminate Access", "Are you sure you want to terminate this user's access?")) return;
    try {
      await axios.post(`${API}/admin/trial/terminate`, { user_id: userId }, { headers });
      await fetchUsers();
      await fetchStats();
    } catch (err) {
      console.error("Error terminating trial:", err);
      alert(err.response?.data?.detail || "Failed to terminate trial");
    }
  };

  const [pausingCountdown, setPausingCountdown] = useState(false);
  const [countdownPaused, setCountdownPaused] = useState(false);

  const fetchCountdownStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/countdown`, { headers });
      setCountdownPaused(!!res.data.paused);
    } catch (err) {
      console.error("Error fetching countdown status:", err);
    }
  };

  const toggleGlobalPause = async () => {
    setPausingCountdown(true);
    try {
      const endpoint = countdownPaused ? "resume" : "pause";
      const res = await axios.post(`${API}/admin/countdown/${endpoint}`, {}, { headers });
      setCountdownPaused(!!res.data.paused);
      if (res.data.message) toast.success(res.data.message);
    } catch (err) {
      console.error("Error toggling global pause:", err);
      alert(err.response?.data?.detail || "Failed to toggle countdown pause");
    }
    setPausingCountdown(false);
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, { headers });
      setStats(asObject(res.data));
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  };

  const fetchMaintenanceStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/maintenance`, { headers });
      setMaintenanceMode(res.data.enabled || false);
      setMaintenanceMessage(res.data.message || "System is under maintenance. Please try again later.");
    } catch (err) {
      console.error("Error fetching maintenance status:", err);
    }
  };

  const toggleMaintenanceMode = async () => {
    setUpdatingMaintenance(true);
    try {
      const newState = !maintenanceMode;
      await axios.post(`${API}/admin/maintenance`, {
        enabled: newState,
        message: maintenanceMessage
      }, { headers });
      setMaintenanceMode(newState);
      alert(`Maintenance mode ${newState ? 'enabled' : 'disabled'}`);
    } catch (err) {
      alert(err.response?.data?.detail || "Error toggling maintenance mode");
    }
    setUpdatingMaintenance(false);
  };

  // Admin 2FA Functions
  const fetchAdmin2FAStatus = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { headers });
      setAdmin2FAEnabled(res.data.two_fa_enabled || false);
    } catch (err) {
      console.error("Error fetching 2FA status:", err);
    }
  };

  const setupAdmin2FA = async () => {
    setAdmin2FALoading(true);
    setAdmin2FAError("");
    try {
      const res = await axios.post(`${API}/auth/2fa/setup`, {}, { headers });
      setAdmin2FASetup(res.data);
    } catch (err) {
      setAdmin2FAError(err.response?.data?.detail || "Failed to setup 2FA");
    }
    setAdmin2FALoading(false);
  };

  const verifyAdmin2FA = async () => {
    if (!admin2FACode || admin2FACode.length !== 6) {
      setAdmin2FAError("Please enter a valid 6-digit code");
      return;
    }
    setAdmin2FALoading(true);
    setAdmin2FAError("");
    try {
      await axios.post(`${API}/auth/2fa/verify`, { code: admin2FACode }, { headers });
      setAdmin2FAEnabled(true);
      setAdmin2FASetup(null);
      setAdmin2FACode("");
      alert("2FA enabled successfully!");
    } catch (err) {
      setAdmin2FAError(err.response?.data?.detail || "Invalid code");
    }
    setAdmin2FALoading(false);
  };

  const disableAdmin2FA = async () => {
    if (!admin2FACode || admin2FACode.length !== 6) {
      setAdmin2FAError("Please enter your current 2FA code to disable");
      return;
    }
    if (!await showConfirm("Disable 2FA", "Are you sure you want to disable 2FA? This will reduce account security.")) return;
    setAdmin2FALoading(true);
    setAdmin2FAError("");
    try {
      await axios.post(`${API}/auth/2fa/disable`, { code: admin2FACode }, { headers });
      setAdmin2FAEnabled(false);
      setAdmin2FACode("");
      alert("2FA disabled");
    } catch (err) {
      setAdmin2FAError(err.response?.data?.detail || "Invalid code");
    }
    setAdmin2FALoading(false);
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { headers });
      setUsers(asArray(res.data?.users));
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const fetchDomains = async () => {
    try {
      const res = await axios.get(`${API}/admin/domains/pool`, { headers });
      setDomains(asArray(res.data?.domains));
      setDomainGroups(asArray(res.data?.groups));
    } catch (err) {
      console.error("Error fetching domains:", err);
    }
  };

  const [customDomainsList, setCustomDomainsList] = useState([]);
  const [cdBusy, setCdBusy] = useState(null);
  const fetchCustomDomains = async () => {
    try {
      const res = await axios.get(`${API}/admin/custom-domains`, { headers });
      setCustomDomainsList(asArray(res.data?.domains));
    } catch (err) {
      console.error("Error fetching custom domains:", err);
    }
  };

  const retryCustomDomainSSL = async (cd) => {
    setCdBusy(cd.id);
    try {
      const res = await axios.post(`${API}/admin/custom-domains/${cd.id}/retry-ssl`, {}, { headers });
      if (res.data.success === false) alert(res.data.message || "DNS not pointing to us yet");
      else await showConfirm("SSL Retry Started", `Provisioning ${cd.domain}. Refresh in a minute to see the result.`, { hideCancel: true }).catch(() => {});
    } catch (err) {
      alert(err.response?.data?.detail || "Retry failed");
    }
    setTimeout(fetchCustomDomains, 3000);
    setCdBusy(null);
  };

  const revokeCustomDomain = async (cd) => {
    if (!await showConfirm("Revoke Custom Domain", `Permanently remove ${cd.domain} (owner: ${cd.owner_username})? This unassigns it and deletes its links. This cannot be undone.`)) return;
    setCdBusy(cd.id);
    try {
      await axios.delete(`${API}/admin/custom-domains/${cd.id}`, { headers });
      await fetchCustomDomains();
    } catch (err) {
      alert(err.response?.data?.detail || "Revoke failed");
    }
    setCdBusy(null);
  };


  const fetchDomainGroups = async () => {
    try {
      const res = await axios.get(`${API}/admin/domains/groups`, { headers });
      setDomainGroups(asArray(res.data?.groups));
    } catch (err) {
      console.error("Error fetching domain groups:", err);
    }
  };

  const fetchAssignments = async () => {
    try {
      const res = await axios.get(`${API}/admin/domains/assignments`, { headers });
      setAssignments(asArray(res.data?.assignments));
    } catch (err) {
      console.error("Error fetching assignments:", err);
    }
  };

  const fetchTickets = async () => {
    try {
      const res = await axios.get(`${API}/support/admin/tickets`, { headers });
      setTickets(asArray(res.data?.tickets));
    } catch (err) {
      console.error("Error fetching tickets:", err);
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications`, { headers });
      setNotifications(asArray(res.data?.notifications));
    } catch (err) {
      console.error("Error fetching notifications:", err);
    }
  };

  const fetchAliasPaths = async () => {
    try {
      const res = await axios.get(`${API}/admin/alias-paths/with-groups`, { headers });
      setAliasPaths(asArray(res.data?.paths));
      setPathGroups(asArray(res.data?.groups));
    } catch (err) {
      console.error("Error fetching alias paths:", err);
      // Fallback to old endpoint
      try {
        const res = await axios.get(`${API}/admin/alias-paths`, { headers });
        setAliasPaths(asArray(res.data?.alias_paths));
      } catch (e) {
        console.error("Fallback also failed:", e);
      }
    }
  };

  const fetchPathGroups = async () => {
    try {
      const res = await axios.get(`${API}/admin/alias-path-groups`, { headers });
      setPathGroups(asArray(res.data?.groups));
    } catch (err) {
      console.error("Error fetching path groups:", err);
    }
  };

  const fetchLicensedUsersForAssign = async () => {
    try {
      const res = await axios.get(`${API}/admin/licensed-users-for-assignment`, { headers });
      setLicensedUsersForAssign(asArray(res.data?.users));
    } catch (err) {
      console.error("Error fetching licensed users:", err);
    }
  };

  const fetchGroupUsers = async (groupId) => {
    try {
      const res = await axios.get(`${API}/admin/alias-path-groups/${groupId}/users`, { headers });
      setGroupUsers(asArray(res.data?.assignments));
    } catch (err) {
      console.error("Error fetching group users:", err);
    }
  };

  const fetchPendingTransactions = async () => {
    try {
      const endpoint = transactionFilter === "pending" 
        ? `${API}/billing/admin/pending`
        : `${API}/billing/admin/transactions`;
      const res = await axios.get(endpoint, { headers });
      setPendingTransactions(asArray(res.data?.transactions));
    } catch (err) {
      console.error("Error fetching transactions:", err);
    }
  };

  const fetchWalletSettings = async () => {
    try {
      const response = await axios.get(`${API}/billing/wallets`, { headers });
      setWalletSettings(asObject(response.data?.wallets, { btc: "", usdt: "" }));
    } catch (error) {
      console.error("Error fetching wallet settings:", error);
    }
  };

  const saveWalletSettings = async () => {
    setWalletSaving(true);
    setWalletMessage("");
    try {
      const response = await axios.put(`${API}/billing/admin/wallets`, walletSettings, { headers });
      setWalletSettings(response.data.wallets);
      setWalletMessage("Wallets updated everywhere. New QR codes are live now.");
      toast.success("Payment wallets updated", { description: "Billing and payment pages received the new addresses." });
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = Array.isArray(detail) ? detail[0]?.msg : detail;
      setWalletMessage(message || "Failed to update wallets.");
      toast.error("Wallet update failed", { description: message || "Check both wallet addresses and try again." });
    }
    setWalletSaving(false);
  };

  const fetchServers = async () => {
    try {
      const res = await axios.get(`${API}/admin/servers`, { headers });
      const nextServers = asArray(res.data?.servers);
      setServers(nextServers);
      checkAllServersHealth(nextServers);
    } catch (err) {
      console.error("Error fetching servers:", err);
    }
  };

  const fetchFleetParity = async () => {
    setFleetParityLoading(true);
    setFleetParityError("");
    try {
      const response = await axios.get(`${API}/admin/fleet-parity?history_limit=10`, { headers });
      setFleetParity(response.data);
    } catch (error) {
      console.error("Failed to fetch fleet parity:", error);
      setFleetParityError(error.response?.data?.detail || "Fleet parity history could not be loaded");
    }
    setFleetParityLoading(false);
  };

  const runFleetParityAudit = async () => {
    setFleetParityRunning(true);
    setFleetParityError("");
    try {
      await axios.post(`${API}/admin/fleet-parity/run`, {}, { headers });
      for (let attempt = 0; attempt < 30; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        const response = await axios.get(`${API}/admin/fleet-parity?history_limit=10`, { headers });
        setFleetParity(response.data);
        if (!response.data?.active) break;
      }
    } catch (error) {
      console.error("Fleet parity audit failed:", error);
      setFleetParityError(error.response?.data?.detail || "Fleet parity audit could not be started");
    }
    setFleetParityRunning(false);
  };

  const checkServerHealth = async (serverId) => {
    setCheckingHealth(serverId);
    try {
      const res = await axios.get(`${API}/servers/${serverId}/health`, { headers });
      setServerHealth(prev => ({ ...prev, [serverId]: res.data }));
    } catch (err) {
      setServerHealth(prev => ({ ...prev, [serverId]: { status: "offline", latency_ms: null } }));
    } finally {
      setCheckingHealth(null);
    }
  };

  const checkAllServersHealth = async (list) => {
    if (!list || !list.length) return;
    setCheckingHealth("all");
    try {
      const results = await Promise.all(
        list.map(s =>
          axios.get(`${API}/servers/${s.id}/health`, { headers })
            .then(r => [s.id, r.data])
            .catch(() => [s.id, { status: "offline", latency_ms: null }])
        )
      );
      setServerHealth(prev => ({ ...prev, ...Object.fromEntries(results) }));
    } finally {
      setCheckingHealth(null);
    }
  };

  const fetchLandingPageTemplates = async () => {
    try {
      const res = await axios.get(`${API}/admin/deployment/templates`, { headers });
      setLandingPageTemplates(asArray(res.data?.templates));
    } catch (err) {
      console.error("Error fetching templates:", err);
    }
  };

  // Pre-fill the Deployment Wizard with the GitHub URL (PAT-injected by backend
  // only for authenticated super-admin sessions). The PAT itself never lives in
  // the React bundle — it's served at runtime through this endpoint.
  const fetchDeploymentDefaults = async () => {
    try {
      const res = await axios.get(`${API}/admin/deployment/defaults`, { headers });
      setDeploymentForm(prev => ({
        ...prev,
        github_repo_url: prev.github_repo_url || res.data.github_repo_url || "",
        dashboard_url: prev.dashboard_url || res.data.dashboard_url || prev.dashboard_url,
        maxmind_account_id: prev.maxmind_account_id || res.data.maxmind_account_id || "",
        maxmind_license_key: prev.maxmind_license_key || res.data.maxmind_license_key || ""
      }));
    } catch (err) {
      console.error("Error fetching deployment defaults:", err);
    }
  };

  const generateDeploymentScript = async () => {
    setGeneratingScript(true);
    try {
      // Save the maxmind keys globally so they are remembered for next time
      if (deploymentForm.maxmind_account_id || deploymentForm.maxmind_license_key) {
        await axios.post(`${API}/admin/deployment/defaults`, {
          maxmind_account_id: deploymentForm.maxmind_account_id,
          maxmind_license_key: deploymentForm.maxmind_license_key
        }, { headers });
      }

      const domainsArray = deploymentForm.domains
        .split(/[,\n]/)
        .map(d => d.trim())
        .filter(d => d.length > 0);
      
      const res = await axios.post(`${API}/admin/deployment/generate-script`, {
        ...deploymentForm,
        domains: domainsArray
      }, { headers });
      
      setGeneratedScript(res.data.script);
      setGeneratedApiKey(res.data.api_key);
      setGeneratedAgentAdmin({
        username: res.data.agent_admin_username,
        password: res.data.agent_admin_password
      });
      setGeneratedServerId(res.data.server_id);
      setDeploymentStep(2);
      fetchServers(); // Refresh server list
    } catch (err) {
      alert(err.response?.data?.detail || "Error generating script");
    }
    setGeneratingScript(false);
  };

  const resetDeploymentWizard = () => {
    setDeploymentForm({
      server_name: "",
      server_ip: "",
      domains: "",
      dashboard_url: "https://wizard-x-routes.shop/api",
      user_id: "",
      landing_page: "cloudflare",
      redirect_url: "",
      enable_telegram: false,
      telegram_bot_token: "",
      telegram_chat_id: "",
      register_in_pool: false,
      github_repo_url: "",
      maxmind_account_id: "",
      maxmind_license_key: ""
    });
    setGeneratedScript(null);
    setGeneratedApiKey(null);
    setGeneratedAgentAdmin(null);
    setGeneratedServerId(null);
    setDeploymentStep(1);
    setSshCredentials({ ssh_user: "root", ssh_password: "", ssh_port: 22 });
    setDeploymentLogs([]);
    setDeploymentStatus(null);
    setConnectionTested(false);
  };

  const testSshConnection = async () => {
    if (!sshCredentials.ssh_password) {
      alert("Please enter SSH password");
      return;
    }
    setDeploymentStatus("testing");
    setDeploymentLogs(prev => [...prev, "[INFO] Testing SSH connection..."]);
    
    try {
      const res = await axios.post(`${API}/admin/deployment/test-connection`, {
        server_ip: deploymentForm.server_ip,
        ssh_user: sshCredentials.ssh_user,
        ssh_password: sshCredentials.ssh_password,
        ssh_port: sshCredentials.ssh_port
      }, { headers });
      
      if (res.data.success) {
        setDeploymentLogs(prev => [...prev, `[SUCCESS] ${res.data.message}`, `[INFO] Server: ${res.data.server_info}`]);
        setConnectionTested(true);
        setDeploymentStatus(null);
      } else {
        setDeploymentLogs(prev => [...prev, `[ERROR] ${res.data.message}`]);
        setDeploymentStatus("failed");
      }
    } catch (err) {
      setDeploymentLogs(prev => [...prev, `[ERROR] ${err.response?.data?.detail || err.message}`]);
      setDeploymentStatus("failed");
    }
  };

  const deployRemotely = async () => {
    if (!connectionTested) {
      alert("Please test SSH connection first");
      return;
    }
    
    setIsDeploying(true);
    setDeploymentStatus("deploying");
    setDeploymentLogs(prev => [...prev, "[INFO] Starting remote deployment..."]);
    setDeploymentStep(4); // Move to deploying step
    
    try {
      const res = await axios.post(`${API}/admin/deployment/deploy-remote`, {
        server_ip: deploymentForm.server_ip,
        ssh_user: sshCredentials.ssh_user,
        ssh_password: sshCredentials.ssh_password,
        ssh_port: sshCredentials.ssh_port,
        script_content: generatedScript,
        server_id: generatedServerId
      }, { headers, timeout: 900000 }); // 15 minute timeout for deployment
      
      setDeploymentLogs(prev => [...prev, ...res.data.logs]);
      
      if (res.data.status === "success") {
        setDeploymentStatus("success");
        setDeploymentStep(5); // Done
      } else {
        setDeploymentStatus("failed");
      }
    } catch (err) {
      setDeploymentLogs(prev => [...prev, `[ERROR] ${err.response?.data?.detail || err.message}`]);
      setDeploymentStatus("failed");
    }
    
    setIsDeploying(false);
  };

  const copyFilesFromSource = async () => {
    const sourcePassword = prompt("Enter SSH password for COMCAST server (209.38.141.226):");
    if (!sourcePassword) return;
    
    setDeploymentStatus("copying");
    setDeploymentLogs(prev => [...prev, "[INFO] Copying files from COMCAST server..."]);
    
    try {
      const res = await axios.post(`${API}/admin/deployment/copy-files`, {
        source_ip: "209.38.141.226",
        source_password: sourcePassword,
        target_ip: deploymentForm.server_ip,
        target_password: sshCredentials.ssh_password
      }, { headers });
      
      setDeploymentLogs(prev => [...prev, ...res.data.logs]);
      
      if (res.data.status === "success") {
        setDeploymentLogs(prev => [...prev, "[SUCCESS] Files copied successfully! Restart the service with: systemctl restart redirector"]);
      }
    } catch (err) {
      setDeploymentLogs(prev => [...prev, `[ERROR] ${err.response?.data?.detail || err.message}`]);
    }
    setDeploymentStatus(null);
  };

  const initialLoadRef = useRef(null);
  const pendingTransactionsRef = useRef(fetchPendingTransactions);
  const defenseRefreshRef = useRef({ checkAllServersHealth, fetchDefenseStats });
  const ja3CandidatesRef = useRef(fetchJa3Candidates);
  const userRefreshRef = useRef({ fetchUsers, fetchPendingTrials });

  initialLoadRef.current = () => Promise.all([fetchStats(), fetchUsers(), fetchDomains(), fetchAssignments(), fetchTickets(), fetchNotifications(), fetchAliasPaths(), fetchPendingTransactions(), fetchWalletSettings(), fetchServers(), fetchFleetParity(), fetchPendingTrials(), fetchPathGroups(), fetchLicensedUsersForAssign(), fetchLandingPageTemplates(), fetchMaintenanceStatus(), fetchAdmin2FAStatus(), fetchDeploymentDefaults(), fetchCountdownStatus(), fetchCustomDomains()]);
  pendingTransactionsRef.current = fetchPendingTransactions;
  defenseRefreshRef.current = { checkAllServersHealth, fetchDefenseStats };
  ja3CandidatesRef.current = fetchJa3Candidates;
  userRefreshRef.current = { fetchUsers, fetchPendingTrials };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await initialLoadRef.current();
      setLoading(false);
    };
    loadData();
  }, [token]);

  useEffect(() => {
    if (!loading) {
      pendingTransactionsRef.current();
    }
  }, [loading, transactionFilter]);

  // Live admin queue: refresh pending payments the moment a user submits one
  useEffect(() => {
    const onNewPending = () => {
      fetchPendingTransactions();
      fetchStats();
    };
    window.addEventListener("admin:new-pending", onNewPending);
    return () => window.removeEventListener("admin:new-pending", onNewPending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, transactionFilter]);

  // Auto-refresh live agent health every 30s when on the Redirect Servers tab
  useEffect(() => {
    if (activeTab !== "servers") return;
    defenseRefreshRef.current.fetchDefenseStats();
    const interval = setInterval(() => {
      defenseRefreshRef.current.checkAllServersHealth(servers);
      defenseRefreshRef.current.fetchDefenseStats();
    }, 30000);
    return () => clearInterval(interval);
  }, [activeTab, token, servers]);

  // Load JA3 auto-learn candidates when opening User Management
  useEffect(() => {
    if (activeTab !== "users") return;
    ja3CandidatesRef.current();
  }, [activeTab, token]);

  // Auto-refresh users & trials every 15s when on users tab
  useEffect(() => {
    if (activeTab !== "users") return;
    const interval = setInterval(() => {
      userRefreshRef.current.fetchUsers();
      userRefreshRef.current.fetchPendingTrials();
    }, 15000);
    return () => clearInterval(interval);
  }, [activeTab, token]);

  // User Actions
  const toggleLicense = async (userId, isLicensed, planId = null) => {
    try {
      const data = {
        user_id: userId,
        is_licensed: !isLicensed
      };
      // If granting license, require plan_id
      if (!isLicensed && planId) {
        data.plan_id = planId;
      }
      await axios.post(`${API}/admin/users/license`, data, { headers });
      fetchUsers();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.detail || "Error updating license");
    }
  };

  const grantLicenseWithPlan = async (userId, planId) => {
    try {
      await axios.post(`${API}/admin/users/license`, {
        user_id: userId,
        is_licensed: true,
        plan_id: planId
      }, { headers });
      fetchUsers();
      fetchStats();
      fetchPendingTrials();
      const planNames = { '2weeks': '2 Weeks', '1month': '1 Month', '6months': '6 Months' };
      alert(`License granted successfully with ${planNames[planId] || planId} subscription`);
    } catch (err) {
      alert(err.response?.data?.detail || "Error granting license");
    }
  };

  const toggleStatus = async (userId, isActive) => {
    try {
      const newActive = !isActive;
      await axios.post(`${API}/admin/users/status`, {
        user_id: userId,
        is_active: newActive
      }, { headers });
      fetchUsers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map(d => d.msg || d).join(", ") : "Error updating status";
      alert(msg);
    }
  };

  const deleteUser = async (userId) => {
    if (!await showConfirm("Delete User", "Are you sure you want to delete this user?")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers });
      fetchUsers();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.detail || "Error deleting user");
    }
  };

  const impersonateUser = async (userId) => {
    try {
      const res = await axios.post(`${API}/admin/impersonate/${userId}`, {}, { headers });
      const { access_token } = res.data;
      // Open in new tab with the impersonated token stored in sessionStorage to avoid logging out the admin
      const newTab = window.open("about:blank", "_blank");
      if (newTab) {
        newTab.document.write(`<html><body><script>
          sessionStorage.setItem("token", "${access_token}");
          window.location.href = "/";
        <\/script></body></html>`);
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Error logging in as user");
    }
  };

  const extendSubscription = async () => {
    if (!extendModal.user || extendDays === 0 || !extendDays) return;
    setExtendLoading(true);
    try {
      const res = await axios.post(`${API}/admin/users/extend-subscription`, {
        user_id: extendModal.user.id,
        days: extendDays
      }, { headers });
      alert(res.data.message);
      setExtendModal({ show: false, user: null });
      setExtendDays(14);
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.detail || "Error updating subscription");
    }
    setExtendLoading(false);
  };

  const resetUserPassword = async (newPassword) => {
    const target = passwordResetModal.user;
    if (!target) return;
    setPasswordResetLoading(true);
    setPasswordResetError("");
    try {
      const response = await axios.post(
        `${API}/admin/users/${target.id}/reset-password`,
        { new_password: newPassword },
        { headers }
      );
      setPasswordResetModal({ show: false, user: null });
      toast.success(response.data.message || `Password reset for ${target.username}`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((item) => item.msg || "Invalid password").join(". ")
          : "Password reset failed";
      setPasswordResetError(message);
    } finally {
      setPasswordResetLoading(false);
    }
  };

  const toggleMultiUrlEntitlement = async (user) => {
    const isEnabled = !!(user.entitlements && user.entitlements.multi_destination_urls);
    const nextEnabled = !isEnabled;
    if (!nextEnabled) {
      const confirmed = await showConfirm(
        "Revoke Multi-URL Destinations?",
        `Disabling this for ${user.username} will truncate their saved destination URLs to the first URL only. Continue?`
      );
      if (!confirmed) return;
    }
    try {
      await axios.post(
        `${API}/admin/users/entitlement`,
        { user_id: user.id, key: "multi_destination_urls", enabled: nextEnabled },
        { headers }
      );
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update entitlement");
    }
  };



  const cleanupExpiredUsers = async () => {
    const confirmed = await showConfirm(
      "Cleanup Expired Users",
      "This will release all domains and delete all links for users with expired licenses. Domains will be returned to the pool. Continue?"
    );
    if (!confirmed) return;
    setCleanupLoading(true);
    setCleanupResult(null);
    try {
      const res = await axios.post(`${API}/admin/cleanup-expired-users`, {}, { headers });
      setCleanupResult(res.data);
      fetchUsers();
      fetchDomains();
    } catch (err) {
      alert(err.response?.data?.detail || "Error cleaning up expired users");
    }
    setCleanupLoading(false);
  };

  // Domain Actions
  const addDomain = async () => {
    if (!newDomain) return;
    try {
      await axios.post(`${API}/admin/domains/pool`, {
        domain: newDomain,
        description: newDomainDesc,
        group_id: newDomainGroup || null,
        notes: newDomainNotes,
        inbox_rating: newDomainRating,
        tags: newDomainTags ? newDomainTags.split(",").map(t => t.trim()) : []
      }, { headers });
      setNewDomain("");
      setNewDomainDesc("");
      setNewDomainGroup("");
      setNewDomainNotes("");
      setNewDomainRating("average");
      setNewDomainTags("");
      fetchDomains();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.detail || "Error adding domain");
    }
  };

  const updateDomain = async (domainId, updateData) => {
    try {
      await axios.put(`${API}/admin/domains/pool/${domainId}`, updateData, { headers });
      setEditingDomain(null);
      fetchDomains();
    } catch (err) {
      alert(err.response?.data?.detail || "Error updating domain");
    }
  };

  const [deleteDomainConfirmId, setDeleteDomainConfirmId] = useState(null);
  const [selectedDomainIds, setSelectedDomainIds] = useState([]);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [healthCheckLoading, setHealthCheckLoading] = useState(false);
  const [healthResults, setHealthResults] = useState(null);
  const removeDomain = async (domainId) => {
    console.log("[DEBUG] removeDomain called with:", domainId);
    try {
      await axios.delete(`${API}/admin/domains/pool/${domainId}`, { headers });
      setDeleteDomainConfirmId(null);
      fetchDomains();
      fetchStats();
    } catch (err) {
      console.error("[DEBUG] removeDomain error:", err);
      alert(err.response?.data?.detail || "Error removing domain");
    }
  };

  const requestDomainDelete = async (domain) => {
    const assignedUsers = domain.assigned_users || [];
    if (assignedUsers.length > 0) {
      const names = assignedUsers.map((assignedUser) => assignedUser.username).join(", ");
      const confirmed = await showConfirm(
        "Domain in use — delete permanently?",
        `${domain.domain} is assigned to ${assignedUsers.length} user(s): ${names}. Permanent deletion removes it from the pool, unassigns users, and deletes their links. This cannot be undone.`
      );
      if (confirmed) removeDomain(domain.id);
      return;
    }
    setDeleteDomainConfirmId(domain.id);
  };

  const bulkDeleteDomains = async () => {
    if (selectedDomainIds.length === 0) return;
    const confirmed = await showConfirm(
      "Bulk Delete Domains",
      `Permanently delete ${selectedDomainIds.length} domain(s)? This will also remove all assignments and links. This action cannot be undone.`
    );
    if (!confirmed) return;
    setBulkDeleteLoading(true);
    try {
      const res = await axios.post(`${API}/admin/domains/pool/bulk-delete`, {
        domain_ids: selectedDomainIds
      }, { headers });
      setSelectedDomainIds([]);
      fetchDomains();
      fetchStats();
      alert(res.data.message);
    } catch (err) {
      alert(err.response?.data?.detail || "Error deleting domains");
    }
    setBulkDeleteLoading(false);
  };

  const toggleDomainSelection = (domainId) => {
    setSelectedDomainIds(prev =>
      prev.includes(domainId) ? prev.filter(id => id !== domainId) : [...prev, domainId]
    );
  };

  const toggleSelectAllDomains = (filteredDomains) => {
    const filteredIds = filteredDomains.map(d => d.id);
    const allSelected = filteredIds.every(id => selectedDomainIds.includes(id));
    if (allSelected) {
      setSelectedDomainIds(prev => prev.filter(id => !filteredIds.includes(id)));
    } else {
      setSelectedDomainIds(prev => [...new Set([...prev, ...filteredIds])]);
    }
  };

  const checkDomainsHealth = async (domainIds) => {
    if (!domainIds || domainIds.length === 0) return;
    setHealthCheckLoading(true);
    setHealthResults(null);
    try {
      const res = await axios.post(`${API}/admin/domains/pool/check-health`, {
        domain_ids: domainIds,
        clear_flags: true
      }, { headers, timeout: 300000 });
      setHealthResults(res.data.results);
      // Refresh domains to show updated tags
      fetchDomains();
    } catch (err) {
      alert(err.response?.data?.detail || "Error checking domain health");
    }
    setHealthCheckLoading(false);
  };

  // Domain Group Actions
  const addDomainGroup = async () => {
    if (!newGroupName) return;
    try {
      await axios.post(`${API}/admin/domains/groups`, {
        name: newGroupName,
        description: newGroupDesc,
        color: newGroupColor
      }, { headers });
      setNewGroupName("");
      setNewGroupDesc("");
      setNewGroupColor("#22d3ee");
      setShowGroupForm(false);
      fetchDomainGroups();
      fetchDomains();
    } catch (err) {
      alert(err.response?.data?.detail || "Error creating group");
    }
  };

  const deleteDomainGroup = async (groupId) => {
    if (!await showConfirm("Delete Group", "Delete this group? Domains will be ungrouped.")) return;
    try {
      await axios.delete(`${API}/admin/domains/groups/${groupId}`, { headers });
      fetchDomainGroups();
      fetchDomains();
    } catch (err) {
      alert(err.response?.data?.detail || "Error deleting group");
    }
  };

  // Assignment Actions
  const assignDomain = async () => {
    console.log("[DEBUG] assignDomain called with:", { selectedUser, selectedDomain });
    if (!selectedUser || !selectedDomain) {
      alert("Please select both a user and a domain");
      return;
    }
    try {
      console.log("[DEBUG] Making POST request to assign domain");
      await axios.post(`${API}/admin/domains/assign`, {
        user_id: selectedUser,
        domain_id: selectedDomain
      }, { headers });
      alert("Domain assigned successfully!");
      setSelectedUser("");
      setSelectedDomain("");
      fetchAssignments();
      fetchStats();
    } catch (err) {
      console.error("[DEBUG] Error:", err);
      alert(err.response?.data?.detail || "Error assigning domain");
    }
  };

  const assignDomainsFromGroup = async () => {
    if (!selectedUser || !selectedGroupForAssign) return;
    try {
      const res = await axios.post(`${API}/admin/domains/assign-from-group`, {
        user_id: selectedUser,
        group_id: selectedGroupForAssign,
        count: assignCount
      }, { headers });
      alert(`Success! ${res.data.message}\nDomains assigned: ${res.data.assigned_domains.join(", ")}`);
      setSelectedUser("");
      setSelectedGroupForAssign("");
      setAssignCount(3);
      fetchAssignments();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.detail || "Error assigning domains from group");
    }
  };

  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  
  const unassignDomain = async (assignmentId) => {
    if (!assignmentId) return;
    try {
      await axios.delete(`${API}/admin/domains/assign/${assignmentId}`, { headers });
      fetchAssignments();
      fetchStats();
      fetchDomains();
      setDeleteConfirmId(null);
    } catch (err) {
      alert(err.response?.data?.detail || "Error removing assignment");
    }
  };

  // Quick Assign from Domain Card
  const quickAssignDomainToUser = async () => {
    if (!quickAssignDomain || !quickAssignUser) return;
    try {
      await axios.post(`${API}/admin/domains/assign`, {
        user_id: quickAssignUser,
        domain_id: quickAssignDomain
      }, { headers });
      alert("Domain assigned successfully!");
      setQuickAssignDomain(null);
      setQuickAssignUser("");
      fetchAssignments();
      fetchDomains();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.detail || "Error assigning domain");
    }
  };

  // Server Actions
  const createServer = async () => {
    if (!newServer.name || !newServer.ip_address) return;
    try {
      const res = await axios.post(`${API}/admin/servers`, newServer, { headers });
      setShowNewApiKey(res.data.server.api_key);
      setNewServer({ name: "", ip_address: "", description: "" });
      fetchServers();
    } catch (err) {
      alert(err.response?.data?.detail || "Error creating server");
    }
  };

  const deleteServer = async (serverId) => {
    if (!await showConfirm("Delete Server", "Delete this server? This will unassign all domains.")) return;
    try {
      await axios.delete(`${API}/admin/servers/${serverId}`, { headers });
      fetchServers();
    } catch (err) {
      alert(err.response?.data?.detail || "Error deleting server");
    }
  };

  const regenerateServerKey = async (serverId) => {
    if (!await showConfirm("Regenerate API Key", "Regenerate API key? The old key will stop working immediately.")) return;
    try {
      const res = await axios.post(`${API}/admin/servers/${serverId}/regenerate-key`, {}, { headers });
      if (res.data.api_key) {
        setShowNewApiKey(res.data.api_key);
        setLastGeneratedKey({ serverId, key: res.data.api_key, timestamp: new Date().toISOString() });
        fetchServers();
      } else {
        alert("Error: No API key returned from server");
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Error regenerating key: " + err.message);
    }
  };

  const assignDomainToServer = async () => {
    if (!serverDomainAssign.server_id || !serverDomainAssign.domain_id) return;
    try {
      await axios.post(
        `${API}/admin/servers/${serverDomainAssign.server_id}/assign-domain`,
        { server_id: serverDomainAssign.server_id, domain_id: serverDomainAssign.domain_id },
        { headers }
      );
      setServerDomainAssign({ server_id: "", domain_id: "" });
      fetchServers();
      fetchDomains();
    } catch (err) {
      alert(err.response?.data?.detail || "Error assigning domain to server");
    }
  };

  const unassignDomainFromServer = async (serverId, domainId) => {
    if (!await showConfirm(
      "Permanently remove domain?",
      "This deletes it from the shared pool, all user assignments, and all campaign links using it. This cannot be undone."
    )) return;
    try {
      const response = await axios.delete(`${API}/admin/servers/${serverId}/domains/${domainId}`, { headers });
      toast.success(response.data.message || "Domain permanently removed");
      await Promise.all([fetchServers(), fetchDomains(), fetchAssignments()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error removing domain");
    }
  };

  const testServerConnection = async (serverId) => {
    setTestingConnection(serverId);
    setConnectionResult(null);
    try {
      const res = await axios.post(`${API}/admin/servers/${serverId}/test-connection`, {}, { headers });
      setConnectionResult({ serverId, ...res.data });
    } catch (err) {
      setConnectionResult({ 
        serverId, 
        success: false, 
        status: "error", 
        message: err.response?.data?.detail || "Connection test failed" 
      });
    }
    setTestingConnection(null);
  };

  const openRebalanceHelper = async (server) => {
    setRebalanceLoading(true);
    setRebalanceModal({ open: true, source: server, plan: null, domainId: "", targetId: "", result: null, error: "" });
    try {
      const response = await axios.get(`${API}/admin/servers/${server.id}/rebalance-plan`, { headers });
      setRebalanceModal((current) => ({
        ...current,
        plan: response.data,
        domainId: response.data.recommendations?.[0]?.domain_id || "",
        targetId: response.data.suggested_target?.id || "",
      }));
    } catch (error) {
      setRebalanceModal((current) => ({ ...current, error: error.response?.data?.detail || "Failed to build rebalance plan." }));
    }
    setRebalanceLoading(false);
  };

  const executeRebalance = async () => {
    setRebalanceLoading(true);
    setRebalanceModal((current) => ({ ...current, error: "", result: null }));
    try {
      const response = await axios.post(
        `${API}/admin/servers/${rebalanceModal.source.id}/rebalance-domain`,
        { domain_id: rebalanceModal.domainId, target_server_id: rebalanceModal.targetId },
        { headers }
      );
      setRebalanceModal((current) => ({ ...current, result: response.data }));
      await fetchServers();
    } catch (error) {
      setRebalanceModal((current) => ({ ...current, error: typeof error.response?.data?.detail === "string" ? error.response.data.detail : "Rebalance failed and was rolled back." }));
    }
    setRebalanceLoading(false);
  };

  const syncFingerproxy = async (serverId = null) => {
    const syncKey = serverId || "all";
    setFingerproxySyncing(syncKey);
    setFingerproxyResult(null);
    try {
      const url = serverId
        ? `${API}/admin/servers/${serverId}/fingerproxy-sync`
        : `${API}/admin/servers/fingerproxy-sync-all`;
      const response = await axios.post(url, {}, { headers, timeout: 320000 });
      setFingerproxyResult(response.data);
      await fetchServers();
      setFingerproxySyncing(null);
      return response.data;
    } catch (error) {
      setFingerproxyResult({ success: false, error: error.response?.data?.detail || "Fingerproxy sync failed." });
      setFingerproxySyncing(null);
      return null;
    }
  };

  const setupDomainsOnServer = async (serverId, domainsList) => {
    if (!setupSshPassword) { alert("SSH password required"); return; }
    setSetupDomainsLoading(true);
    setSetupDomainsResult(null);
    try {
      const res = await axios.post(`${API}/admin/servers/${serverId}/setup-domains-batch`, {
        domains: domainsList,
        ssh_password: setupSshPassword
      }, { headers });
      setSetupDomainsResult(res.data);
      setSetupSshPassword("");
    } catch (err) {
      setSetupDomainsResult({ status: "failed", logs: [err.response?.data?.detail || "Setup failed"] });
    }
    setSetupDomainsLoading(false);
  };

  const [addDomainDeploying, setAddDomainDeploying] = useState(false);
  const [addDomainLogs, setAddDomainLogs] = useState(null);
  const [addDomainSshPassword, setAddDomainSshPassword] = useState("");
  const [pendingDomainAdd, setPendingDomainAdd] = useState(null); // { serverId, domain }
  const [repairingDomainMetadata, setRepairingDomainMetadata] = useState(null);
  const [domainMetadataRepair, setDomainMetadataRepair] = useState(null);

  const repairServerDomainMetadata = async (serverId) => {
    setRepairingDomainMetadata(serverId);
    setDomainMetadataRepair(null);
    try {
      const response = await axios.post(
        `${API}/admin/servers/${serverId}/repair-domain-metadata`,
        {},
        { headers }
      );
      setDomainMetadataRepair({ serverId, ...response.data });
      toast.success(`${response.data.domains.length} deployed domain(s) restored to the pool`);
      await Promise.all([fetchServers(), fetchDomains()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Domain metadata recovery failed");
    }
    setRepairingDomainMetadata(null);
  };

  const addDomainToServer = async (serverId) => {
    if (!newDomainInput.trim()) return;
    // Strip trailing dots from domain
    const cleanDomain = newDomainInput.trim().replace(/\.+$/, '');
    if (!cleanDomain) return;
    // Show SSH password prompt
    setPendingDomainAdd({ serverId, domain: cleanDomain });
    setAddDomainLogs(null);
  };

  const confirmAddDomainToServer = async () => {
    if (!pendingDomainAdd || !addDomainSshPassword) return;
    const { serverId, domain } = pendingDomainAdd;
    const server = servers.find(s => s.id === serverId);
    const serverIp = server?.ip_address || "";
    setAddDomainDeploying(true);
    setAddDomainLogs(["[INFO] Checking DNS resolution..."]);
    try {
      // Step 0: DNS Pre-Check
      const dnsRes = await axios.post(`${API}/admin/check-dns`, {
        domain,
        expected_ip: serverIp
      }, { headers });

      const dns = dnsRes.data;
      if (dns.status === "ok") {
        setAddDomainLogs(prev => [...prev, `[DNS OK] ${dns.message}`]);
      } else if (dns.status === "mismatch") {
        setAddDomainLogs(prev => [...prev, `[DNS WARNING] ${dns.message}`, "[INFO] Nginx will be configured but SSL will likely fail. Fix the DNS A record first."]);
        setAddDomainDeploying(false);
        return;
      } else {
        setAddDomainLogs(prev => [...prev, `[DNS FAIL] ${dns.message}`, `[INFO] Add an A record for '${domain}' pointing to ${serverIp} in your DNS provider, then retry.`]);
        setAddDomainDeploying(false);
        return;
      }

      // Step 1: Add domain to DB
      setAddDomainLogs(prev => [...prev, "[INFO] Adding domain to database..."]);
      await axios.post(`${API}/admin/servers/${serverId}/add-domain`, {
        domain
      }, { headers });
      setAddDomainLogs(prev => [...prev, "[SUCCESS] Domain added to database", "[INFO] Setting up Nginx + SSL on server... (this can take up to a minute)"]);

      // Step 2: Auto-deploy Nginx + SSL (background job — poll for result)
      const startRes = await axios.post(`${API}/admin/servers/${serverId}/setup-domain`, {
        domain,
        ssh_password: addDomainSshPassword,
        ssh_user: "root",
        ssh_port: 22
      }, { headers });

      const jobId = startRes.data.job_id;
      let done = false;
      let attempts = 0;
      while (!done && attempts < 90) {
        await new Promise(r => setTimeout(r, 2000));
        attempts++;
        try {
          const st = await axios.get(`${API}/admin/deploy-jobs/${jobId}`, { headers });
          if (st.data.status && st.data.status !== "running") {
            setAddDomainLogs(prev => [...prev, ...(st.data.logs || [])]);
            if (st.data.status === "success" || st.data.status === "partial") {
              setNewDomainInput("");
              fetchServers();
              fetchDomains();
            }
            done = true;
          }
        } catch (pollErr) {
          // transient network/timeout during polling — keep trying
        }
      }
      if (!done) {
        setAddDomainLogs(prev => [...prev, "[INFO] Deployment is still running on the server. Refresh Redirect Servers shortly to confirm the domain."]);
        fetchServers();
        fetchDomains();
      }
    } catch (err) {
      setAddDomainLogs(prev => [...prev, `[ERROR] ${err.response?.data?.detail || err.message}`]);
    }
    setAddDomainDeploying(false);
  };

  const cancelAddDomain = () => {
    setPendingDomainAdd(null);
    setAddDomainSshPassword("");
    setAddDomainLogs(null);
  };

  const fetchServerStats = async (serverId) => {
    try {
      const res = await axios.get(`${API}/admin/servers/${serverId}/stats`, { headers });
      setServerStats(res.data);
      setShowServerStats(serverId);
    } catch (err) {
      alert(err.response?.data?.detail || "Error fetching server stats");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  // Support Actions
  const respondToTicket = async (ticketId) => {
    if (!ticketResponse) return;
    try {
      await axios.post(`${API}/support/admin/tickets/${ticketId}/respond`, {
        response: ticketResponse,
        status: "responded"
      }, { headers });
      setTicketResponse("");
      setSelectedTicket(null);
      fetchTickets();
      alert("Response sent successfully!");
    } catch (err) {
      alert(err.response?.data?.detail || err.message || "Error responding to ticket");
    }
  };

  const closeTicket = async (ticketId) => {
    if (!confirm("Are you sure you want to close this ticket?")) return;
    try {
      await axios.post(`${API}/support/admin/tickets/${ticketId}/close`, {}, { headers });
      fetchTickets();
      alert("Ticket closed successfully!");
    } catch (err) {
      alert(err.response?.data?.detail || err.message || "Error closing ticket");
    }
  };

  // Notification Actions
  const sendNotification = async () => {
    if (!newNotification.title || !newNotification.message) return;
    try {
      await axios.post(`${API}/admin/notifications`, newNotification, { headers });
      setNewNotification({ title: "", message: "", type: "info" });
      fetchNotifications();
    } catch (err) {
      alert(err.response?.data?.detail || "Error sending notification");
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/admin/notifications/${notificationId}`, { headers });
      fetchNotifications();
    } catch (err) {
      console.error("Error deleting notification:", err);
    }
  };

  // Alias Path Actions
  const addAliasPath = async (groupId = null) => {
    const pathToAdd = groupId ? newPathForGroup : newAliasPath;
    if (!pathToAdd) return;
    try {
      await axios.post(`${API}/admin/alias-paths`, { path: pathToAdd, group_id: groupId }, { headers });
      if (groupId) {
        setNewPathForGroup("");
      } else {
        setNewAliasPath("");
      }
      fetchAliasPaths();
      fetchPathGroups();
    } catch (err) {
      alert(err.response?.data?.detail || "Error adding alias path");
    }
  };

  const updateAliasPath = async (pathId) => {
    if (!editPathValue) return;
    try {
      await axios.put(`${API}/admin/alias-paths/${pathId}`, { path: editPathValue }, { headers });
      setEditingPath(null);
      setEditPathValue("");
      fetchAliasPaths();
    } catch (err) {
      alert(err.response?.data?.detail || "Error updating alias path");
    }
  };

  const deleteAliasPath = async (pathId) => {
    if (!await showConfirm("Delete Alias Path", "Are you sure you want to delete this alias path?")) return;
    try {
      await axios.delete(`${API}/admin/alias-paths/${pathId}`, { headers });
      fetchAliasPaths();
      fetchPathGroups();
    } catch (err) {
      console.error("Error deleting alias path:", err);
    }
  };

  // System Path Generator
  const generatePaths = async () => {
    if (genCount < 1 || genCount > 7000) return;
    setGenLoading(true);
    setGeneratedPaths([]);
    setSelectedGenPaths(new Set());
    try {
      const res = await axios.post(`${API}/admin/alias-paths/generate`, { count: genCount }, { headers });
      setGeneratedPaths(res.data.paths || []);
      // Select all by default
      setSelectedGenPaths(new Set(res.data.paths.map((_, i) => i)));
    } catch (err) {
      alert(err.response?.data?.detail || "Error generating paths");
    }
    setGenLoading(false);
  };

  const toggleGenPath = (idx) => {
    setSelectedGenPaths(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const addSelectedGenPaths = async () => {
    const pathsToAdd = generatedPaths.filter((_, i) => selectedGenPaths.has(i));
    if (pathsToAdd.length === 0) return;
    
    // Bulk add in batches of 100
    let added = 0;
    let errors = 0;
    const batchSize = 100;
    for (let i = 0; i < pathsToAdd.length; i += batchSize) {
      const batch = pathsToAdd.slice(i, i + batchSize);
      for (const path of batch) {
        try {
          await axios.post(`${API}/admin/alias-paths`, { path, group_id: null }, { headers });
          added++;
        } catch {
          errors++;
        }
      }
    }
    alert(`Added ${added} path(s)${errors > 0 ? `, ${errors} failed (duplicates?)` : ''}`);
    setGeneratedPaths([]);
    setSelectedGenPaths(new Set());
    fetchAliasPaths();
  };

  const toggleAliasPath = (id) => {
    setSelectedAliasPaths(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllUngroupedPaths = () => {
    const ungrouped = aliasPaths.filter(p => !p.group_id);
    if (selectedAliasPaths.size === ungrouped.length) {
      setSelectedAliasPaths(new Set());
    } else {
      setSelectedAliasPaths(new Set(ungrouped.map(p => p.id)));
    }
  };

  const bulkDeleteAliasPaths = async () => {
    if (selectedAliasPaths.size === 0) return;
    const confirmed = await showConfirm(
      "Delete Alias Paths",
      `Delete ${selectedAliasPaths.size} selected alias path(s)? This cannot be undone.`
    );
    if (!confirmed) return;
    setBulkDeleteLoading(true);
    try {
      await axios.post(`${API}/admin/alias-paths/bulk-delete`, { path_ids: Array.from(selectedAliasPaths) }, { headers });
      setSelectedAliasPaths(new Set());
      fetchAliasPaths();
    } catch (err) {
      alert(err.response?.data?.detail || "Error deleting paths");
    }
    setBulkDeleteLoading(false);
  };


  const syncAliasPathsToServers = async () => {
    try {
      await axios.post(`${API}/admin/alias-paths/sync`, {}, { headers });
      alert("Alias paths synced to all active servers!");
    } catch (err) {
      alert(err.response?.data?.detail || "Error syncing alias paths");
    }
  };

  // Path Group Actions
  const createPathGroup = async () => {
    if (!newPathGroup.name) return;
    try {
      await axios.post(`${API}/admin/alias-path-groups`, newPathGroup, { headers });
      setNewPathGroup({ name: "", description: "", color: "#f97316" });
      setShowPathGroupForm(false);
      fetchPathGroups();
    } catch (err) {
      alert(err.response?.data?.detail || "Error creating path group");
    }
  };

  const deletePathGroup = async (groupId) => {
    if (!await showConfirm("Delete Path Group", "Delete this path group? All paths will be ungrouped and user assignments removed.")) return;
    try {
      await axios.delete(`${API}/admin/alias-path-groups/${groupId}`, { headers });
      fetchPathGroups();
      fetchAliasPaths();
    } catch (err) {
      alert(err.response?.data?.detail || "Error deleting path group");
    }
  };

  const movePathToGroup = async (pathId, groupId) => {
    try {
      await axios.put(`${API}/admin/alias-paths/${pathId}/move-to-group?group_id=${groupId || ""}`, {}, { headers });
      fetchAliasPaths();
      fetchPathGroups();
    } catch (err) {
      alert(err.response?.data?.detail || "Error moving path");
    }
  };

  const openUserAssignModal = async (groupId) => {
    setAssigningGroupId(groupId);
    setSelectedUsersForAssign([]);
    await fetchLicensedUsersForAssign();
    setShowUserAssignModal(true);
  };

  const assignUsersToGroup = async () => {
    if (!assigningGroupId || selectedUsersForAssign.length === 0) return;
    try {
      const res = await axios.post(`${API}/admin/alias-path-groups/${assigningGroupId}/assign-users`, {
        group_id: assigningGroupId,
        user_ids: selectedUsersForAssign
      }, { headers });
      alert(res.data.message);
      setShowUserAssignModal(false);
      setSelectedUsersForAssign([]);
      fetchPathGroups();
      fetchLicensedUsersForAssign();
    } catch (err) {
      alert(err.response?.data?.detail || "Error assigning users");
    }
  };

  const viewGroupUsers = async (groupId) => {
    setViewingGroupId(groupId);
    await fetchGroupUsers(groupId);
    setShowGroupUsersModal(true);
  };

  const unassignUserFromGroup = async (groupId, userId) => {
    if (!await showConfirm("Remove User", "Remove this user from the path group?")) return;
    try {
      await axios.delete(`${API}/admin/alias-path-groups/${groupId}/users/${userId}`, { headers });
      fetchGroupUsers(groupId);
      fetchPathGroups();
      fetchLicensedUsersForAssign();
    } catch (err) {
      alert(err.response?.data?.detail || "Error unassigning user");
    }
  };

  // Transaction Verification Actions
  const verifyTransaction = async (transactionId, status, adminNote = "") => {
    try {
      await axios.post(`${API}/billing/admin/verify`, {
        transaction_id: transactionId,
        status,
        admin_note: adminNote
      }, { headers });
      fetchPendingTransactions();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.detail || "Error verifying transaction");
    }
  };

  const filteredUsers = users.filter(u => 
    u.username?.toLowerCase().includes(userSearch.toLowerCase())
  ).sort((a, b) => {
    // Licensed users first, then unlicensed
    if (a.is_licensed && !b.is_licensed) return -1;
    if (!a.is_licensed && b.is_licensed) return 1;
    return 0;
  });

  const licensedUsers = users.filter(u => u.is_licensed);
  const pendingApprovalUsers = users.filter(u => !u.is_licensed);
  const normalizedPendingSearch = pendingApprovalSearch.trim().toLowerCase();
  const filteredPendingApprovalUsers = normalizedPendingSearch
    ? pendingApprovalUsers.filter((pendingUser) => (
        pendingUser.username || ""
      ).toLowerCase().includes(normalizedPendingSearch))
    : pendingApprovalUsers;
  const pendingTickets = tickets.filter(t => t.status === "pending" || t.status === "open");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="admin-command-center flex min-w-0 h-[calc(100vh-4rem)] -m-4 bg-[#09090B] lg:-m-8" data-testid="admin-command-center">
      <AdminSecondarySidebar
        activeId={activeTab}
        badges={{ transactions: pendingTransactions.filter((item) => item.status === "pending").length, support: pendingTickets.length }}
        collapsed={sidebarCollapsed}
        items={NAV_ITEMS}
        onSelect={setActiveTab}
        onToggle={() => setSidebarCollapsed((current) => !current)}
        query={adminNavSearch}
        setQuery={setAdminNavSearch}
        stats={{ totalUsers: stats?.total_users || 0, licensed: stats?.licensed_users || 0 }}
      />

      {/* Main Content Area */}
      <main className="min-w-0 flex-1 overflow-x-hidden overflow-y-auto p-3 sm:p-5 lg:p-6" data-testid="admin-main-content">
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <AdminOverviewWorkspace
            assignments={assignments}
            domains={domains}
            maintenance={{ enabled: maintenanceMode, message: maintenanceMessage, setMessage: setMaintenanceMessage, toggle: toggleMaintenanceMode, updating: updatingMaintenance }}
            onNavigate={setActiveTab}
            onRefresh={() => { fetchStats(); fetchUsers(); fetchDomains(); fetchAssignments(); fetchTickets(); fetchPendingTransactions(); fetchServers(); }}
            pendingTransactions={pendingTransactions}
            servers={servers}
            stats={stats}
            tickets={tickets}
          />
        )}

        {activeTab === "users" && (
          <div data-testid="user-management-section"><UserManagementWorkspace
            users={users}
            search={userSearch}
            onSearch={setUserSearch}
            cleanupLoading={cleanupLoading}
            cleanupResult={cleanupResult}
            onCleanup={cleanupExpiredUsers}
            onDismissCleanup={() => setCleanupResult(null)}
            onImpersonate={impersonateUser}
            onOpenSubscription={(selectedAccount) => { setExtendModal({ show: true, user: selectedAccount }); setExtendDays(14); }}
            onOpenPasswordReset={(selectedAccount) => { setPasswordResetError(""); setPasswordResetModal({ show: true, user: selectedAccount }); }}
            onToggleEntitlement={toggleMultiUrlEntitlement}
            onToggleStatus={toggleStatus}
            onDelete={deleteUser}
            defenseProps={{ open: defenseOpen, onToggle: () => { setDefenseOpen((current) => !current); if (!defenseOpen) loadDefenseDefaults(); }, defaults: defenseDefaults, onDefaultsChange: setDefenseDefaults, onApply: applyDefenseDefaults, applying: defenseApplying, message: defenseMsg, candidates: ja3Candidates, reviewing: ja3Reviewing, onReview: reviewJa3 }}
            subscriptionProps={{ modal: extendModal, days: extendDays, onDaysChange: setExtendDays, loading: extendLoading, onSave: extendSubscription, onClose: () => setExtendModal({ show: false, user: null }) }}
            passwordResetProps={{ open: passwordResetModal.show, user: passwordResetModal.user, loading: passwordResetLoading, error: passwordResetError, onSubmit: resetUserPassword, onClose: () => { setPasswordResetError(""); setPasswordResetModal({ show: false, user: null }); } }}
          /></div>
        )}

        {/* License Control Tab */}
        {activeTab === "licenses" && (
          <LicenseControlWorkspace
            countdownPaused={countdownPaused}
            deleteUser={deleteUser}
            grantLicense={grantLicenseWithPlan}
            grantTrial={grantTrial}
            licensedUsers={licensedUsers}
            onPauseToggle={toggleGlobalPause}
            pausing={pausingCountdown}
            pendingUsers={pendingApprovalUsers}
            revokeLicense={toggleLicense}
            terminateTrial={terminateTrial}
          />
        )}
        {false && activeTab === "licenses" && (
          <div className="admin-section" data-testid="license-control-section">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-2xl font-orbitron font-bold text-white">License Control</h1>
                <p className="text-slate-400 text-sm mt-1">Manage user licenses and access</p>
              </div>
              {/* Global countdown pause (freezes ALL users' timers during maintenance) */}
              <div className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border ${countdownPaused ? 'bg-purple-500/10 border-purple-500/40' : 'bg-slate-800/50 border-slate-700'}`}>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wide text-slate-400">All Countdowns</p>
                  <p className={`text-sm font-semibold ${countdownPaused ? 'text-purple-300' : 'text-green-400'}`} data-testid="global-countdown-status">
                    {countdownPaused ? '⏸ Paused (frozen)' : '▶ Running'}
                  </p>
                </div>
                <button
                  onClick={toggleGlobalPause}
                  disabled={pausingCountdown}
                  className={`px-4 py-2 text-sm font-semibold rounded-lg border transition-colors flex items-center gap-2 disabled:opacity-50 ${
                    countdownPaused
                      ? 'bg-green-500/15 text-green-400 border-green-500/40 hover:bg-green-500/25'
                      : 'bg-purple-500/15 text-purple-300 border-purple-500/40 hover:bg-purple-500/25'
                  }`}
                  title={countdownPaused ? "Resume everyone's countdown (frozen time is added back to all users)" : "Freeze every user's trial & subscription countdown"}
                  data-testid="global-pause-toggle"
                >
                  {countdownPaused
                    ? (<><Play className="w-4 h-4" /> Resume All</>)
                    : (<><Pause className="w-4 h-4" /> Pause All</>)}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Licensed Users */}
              <div className="card-dark p-5">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  Licensed Users ({licensedUsers.length})
                </h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {licensedUsers.map(user => (
                    <div key={user.id} className="flex flex-col items-stretch gap-3 p-3 bg-green-500/5 border border-green-500/20 rounded-lg sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                          <span className="text-xs font-semibold text-green-400">{user.username?.charAt(0).toUpperCase()}</span>
                        </div>
                        <div>
                          <p className="text-sm text-white flex items-center gap-2">
                            {user.username}
                            {user.is_on_trial && (
                              <span className="px-1.5 py-0.5 text-xs bg-amber-500/20 text-amber-400 rounded">TRIAL</span>
                            )}
                          </p>
                          <p className="text-xs text-slate-500">
                            {user.is_on_trial && user.trial_end_time && (
                              <span className="text-amber-400">
                                Expires: {new Date(user.trial_end_time).toLocaleString()}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {user.is_on_trial && (
                          <button
                            onClick={() => terminateTrial(user.id)}
                            className="px-3 py-1.5 text-xs bg-orange-500/10 text-orange-400 border border-orange-500/30 rounded-lg hover:bg-orange-500/20 transition-colors"
                            data-testid={`end-trial-${user.id}`}
                          >
                            End Trial
                          </button>
                        )}
                        <button
                          onClick={() => toggleLicense(user.id, true)}
                          className="px-3 py-1.5 text-xs bg-red-500/10 text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/20 transition-colors"
                          data-testid={`revoke-license-${user.id}`}
                        >
                          Revoke
                        </button>
                      </div>
                    </div>
                  ))}
                  {licensedUsers.length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-8">No licensed users</p>
                  )}
                </div>
              </div>

              {/* Unlicensed Users */}
              <div className="card-dark p-5">
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="font-semibold text-white flex items-center gap-2">
                    <Clock className="w-5 h-5 text-yellow-400" />
                    Pending Approval <span className="text-yellow-300" data-testid="pending-approval-count">({pendingApprovalUsers.length})</span>
                  </h3>
                  <span className="text-xs text-slate-500" data-testid="pending-search-results-count">
                    {normalizedPendingSearch ? `${filteredPendingApprovalUsers.length} match${filteredPendingApprovalUsers.length === 1 ? "" : "es"}` : `${pendingApprovalUsers.length} total`}
                  </span>
                </div>
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                  <input
                    type="search"
                    value={pendingApprovalSearch}
                    onChange={(event) => setPendingApprovalSearch(event.target.value)}
                    placeholder="Search pending usernames..."
                    className="input-dark w-full pl-10 pr-10"
                    data-testid="pending-approval-search-input"
                    aria-label="Search pending approval usernames"
                    autoComplete="off"
                  />
                  {pendingApprovalSearch && (
                    <button
                      type="button"
                      onClick={() => setPendingApprovalSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-slate-500 transition-colors hover:bg-slate-800 hover:text-white"
                      data-testid="pending-approval-search-clear-btn"
                      aria-label="Clear pending approval search"
                      title="Clear search"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="pending-approval-list">
                  {filteredPendingApprovalUsers.map(user => (
                    <div key={user.id} className="flex flex-col items-stretch gap-3 p-3 bg-yellow-500/5 border border-yellow-500/20 rounded-lg xl:flex-row xl:items-center xl:justify-between" data-testid={`pending-approval-row-${user.id}`}>
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center">
                          <span className="text-xs font-semibold text-yellow-400">{user.username?.charAt(0).toUpperCase()}</span>
                        </div>
                        <div className="min-w-0">
                          <p className="break-words text-sm text-white" data-testid={`pending-approval-username-${user.id}`}>{user.username}</p>
                          <p className="text-xs text-slate-500" data-testid={`pending-approval-date-${user.id}`}>Registered: {user.created_at ? new Date(user.created_at).toLocaleDateString() : "-"}</p>
                        </div>
                      </div>
                      <div className="flex w-full flex-wrap justify-start gap-2 xl:w-auto xl:justify-end">
                        <button
                          onClick={() => {
                            const days = window.prompt(`How many days of trial for ${user.username}?`, "2");
                            if (days && !isNaN(days) && Number(days) > 0) {
                              grantTrial(user.id, Math.floor(Number(days) * 24));
                            }
                          }}
                          className="px-3 py-1.5 text-xs bg-amber-500/10 text-amber-400 border border-amber-500/30 rounded-lg hover:bg-amber-500/20 transition-colors"
                          data-testid={`custom-trial-${user.id}`}
                        >
                          Custom Trial (Days)
                        </button>
                        <button
                          onClick={() => grantTrial(user.id, 3)}
                          className="px-3 py-1.5 text-xs bg-amber-500/10 text-amber-400 border border-amber-500/30 rounded-lg hover:bg-amber-500/20 transition-colors"
                          data-testid={`grant-3h-trial-${user.id}`}
                        >
                          3h Trial
                        </button>
                        <button
                          onClick={() => grantLicenseWithPlan(user.id, '2weeks')}
                          className="px-3 py-1.5 text-xs bg-blue-500/10 text-blue-400 border border-blue-500/30 rounded-lg hover:bg-blue-500/20 transition-colors"
                          data-testid={`grant-2weeks-${user.id}`}
                        >
                          2 Weeks ($250)
                        </button>
                        <button
                          onClick={() => grantLicenseWithPlan(user.id, '1month')}
                          className="px-3 py-1.5 text-xs bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 rounded-lg hover:bg-cyan-500/20 transition-colors"
                          data-testid={`grant-1month-${user.id}`}
                        >
                          1 Month ($400)
                        </button>
                        <button
                          onClick={() => grantLicenseWithPlan(user.id, '6months')}
                          className="px-3 py-1.5 text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 rounded-lg hover:bg-emerald-500/20 transition-colors"
                          data-testid={`grant-6months-${user.id}`}
                        >
                          6 Months ($1,500)
                        </button>
                        <button
                          onClick={() => deleteUser(user.id)}
                          data-testid={`delete-pending-user-${user.id}`}
                          className="px-3 py-1.5 text-xs bg-red-500/10 text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/20 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                  {pendingApprovalUsers.length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-8" data-testid="pending-approval-empty-state">No pending users</p>
                  )}
                  {pendingApprovalUsers.length > 0 && filteredPendingApprovalUsers.length === 0 && (
                    <div className="py-8 text-center" data-testid="pending-approval-search-empty-state">
                      <Search className="mx-auto h-7 w-7 text-slate-600" />
                      <p className="mt-2 text-sm font-medium text-slate-300">No pending usernames match “{pendingApprovalSearch.trim()}”</p>
                      <button
                        type="button"
                        onClick={() => setPendingApprovalSearch("")}
                        className="mt-3 text-xs font-semibold text-cyan-400 transition-colors hover:text-cyan-300"
                        data-testid="pending-approval-empty-clear-btn"
                      >
                        Clear search
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Redirect Servers Tab */}
        {activeTab === "servers" && (
          <div className="admin-section" data-testid="servers-tab">
            <ServerFleetHeader
              checkingHealth={checkingHealth}
              fingerproxySyncing={fingerproxySyncing}
              onRefreshHealth={() => checkAllServersHealth(servers)}
              onSyncTls={() => syncFingerproxy()}
              servers={servers}
            />

            <FleetParityPanel
              data={fleetParity}
              error={fleetParityError}
              loading={fleetParityLoading}
              onRun={runFleetParityAudit}
              running={fleetParityRunning}
            />

            {fingerproxyResult && (
              <div className={`rounded-md border p-4 text-sm ${fingerproxyResult.success ? "border-green-500/30 bg-green-500/10 text-green-200" : "border-red-500/30 bg-red-500/10 text-red-200"}`} data-testid="fingerproxy-sync-result">
                <p className="font-medium">{fingerproxyResult.success ? "Fingerproxy sync completed" : "Fingerproxy sync needs attention"}</p>
                {fingerproxyResult.results && <p className="mt-1 text-xs opacity-80">{fingerproxyResult.results.filter((item) => item.success).length}/{fingerproxyResult.results.length} agents successful</p>}
                {fingerproxyResult.results?.filter((item) => !item.success).map((item) => (
                  <div key={item.server_id || item.ip_address} className="mt-3 rounded-md border border-red-500/25 bg-red-950/30 p-3" data-testid={`fingerproxy-failed-agent-${item.server_id || "unknown"}`}>
                    <p className="font-semibold text-red-200">{item.server_name || item.ip_address || "Unknown agent"}</p>
                    <p className="mt-1 whitespace-pre-wrap break-words font-mono text-xs text-red-100/80" data-testid={`fingerproxy-failed-agent-error-${item.server_id || "unknown"}`}>{item.error || item.output || "Sync failed without diagnostic output"}</p>
                  </div>
                ))}
                {fingerproxyResult.error && <pre className="mt-2 whitespace-pre-wrap text-xs">{typeof fingerproxyResult.error === "string" ? fingerproxyResult.error : JSON.stringify(fingerproxyResult.error, null, 2)}</pre>}
              </div>
            )}

            {/* Live Bot Defense Stats */}
            {defenseStats && (
              <div className="rounded-lg border border-cyan-500/20 bg-slate-900/65 p-5" data-testid="defense-stats-panel">
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <div className="rounded-md border border-cyan-500/30 bg-cyan-500/10 p-2"><Shield className="h-4 w-4 text-cyan-300" /></div>
                  <div>
                    <h2 className="text-base font-semibold text-white">Live Bot Defense</h2>
                    <p className="text-xs text-slate-500">{defenseStats.totals?.agents_reporting || 0} agent(s) reporting</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 divide-x divide-y divide-slate-800 overflow-hidden rounded-md border border-slate-800 bg-slate-950/55 md:grid-cols-4 md:divide-y-0">
                  {[
                    { label: "Blocks Today", value: defenseStats.totals?.blocks_today || 0, color: "text-cyan-400", testid: "stat-blocks-today" },
                    { label: "Passive Blocks (total)", value: defenseStats.totals?.passive_blocks_total || 0, color: "text-emerald-400", testid: "stat-passive-blocks" },
                    { label: "Behavioral Blocks", value: defenseStats.totals?.behavior_blocks_total || 0, color: "text-purple-400", testid: "stat-behavior-blocks" },
                    { label: "Crawler IP Ranges", value: defenseStats.totals?.crawler_cidr_ranges || 0, color: "text-amber-400", testid: "stat-crawler-ranges" },
                  ].map((c) => (
                    <div key={c.label} className="p-4" data-testid={c.testid}>
                      <p className={`font-mono text-xl font-bold ${c.color}`}>{c.value.toLocaleString()}</p>
                      <p className="mt-1 text-[11px] uppercase text-slate-500">{c.label}</p>
                    </div>
                  ))}
                </div>
                {defenseStats.reasons && Object.keys(defenseStats.reasons).length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Top block reasons</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(defenseStats.reasons).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([k, v]) => (
                        <span key={k} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-800 border border-slate-700 text-xs text-slate-300">
                          <span className="font-mono text-cyan-400">{v}</span>
                          {k}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {(defenseStats.pending_ja3_candidates || 0) > 0 && (
                  <div className="mt-4 flex items-center gap-2 text-sm text-amber-400">
                    <AlertTriangle className="w-4 h-4" />
                    {defenseStats.pending_ja3_candidates} JA3 hash(es) awaiting review in User Management.
                  </div>
                )}
              </div>
            )}

            {/* New API Key Modal */}
            {showNewApiKey && (
              <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
                <div className="bg-slate-900 border border-emerald-500/30 rounded-xl p-6 max-w-lg w-full mx-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                      <Key className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">API Key Generated</h3>
                      <p className="text-xs text-red-400 font-semibold">⚠️ COPY THIS KEY NOW - It will NOT be shown again!</p>
                    </div>
                  </div>
                  <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 font-mono text-sm text-emerald-400 break-all select-all cursor-text">
                    {showNewApiKey}
                  </div>
                  <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-xs text-amber-400">
                      <strong>Add this to your agent's .env file:</strong>
                    </p>
                    <code className="text-xs text-white mt-1 block font-mono">
                      SERVER_API_KEY={showNewApiKey}
                    </code>
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(showNewApiKey);
                        alert("API Key copied to clipboard! Save it somewhere safe.");
                      }}
                      className="btn-primary flex-1 flex items-center justify-center gap-2"
                    >
                      <Copy className="w-4 h-4" />
                      Copy Full Key
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`SERVER_API_KEY=${showNewApiKey}`);
                        alert("Copied .env format to clipboard!");
                      }}
                      className="btn-secondary flex-1 flex items-center justify-center gap-2"
                    >
                      <Copy className="w-4 h-4" />
                      Copy .env Format
                    </button>
                  </div>
                  <button
                    onClick={() => setShowNewApiKey(null)}
                    className="w-full mt-3 py-2 text-slate-400 hover:text-white text-sm"
                  >
                    I've saved the key - Close
                  </button>
                </div>
              </div>
            )}

            {/* Last Generated Key Reference */}
            {lastGeneratedKey && !showNewApiKey && (
              <div className="card-dark p-4 border border-amber-500/30 bg-amber-500/5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Key className="w-5 h-5 text-amber-400" />
                    <div>
                      <p className="text-sm text-white">Last Generated API Key</p>
                      <p className="text-xs text-slate-400">Generated at {new Date(lastGeneratedKey.timestamp).toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="text-xs text-emerald-400 bg-slate-800 px-3 py-1 rounded font-mono">
                      {lastGeneratedKey.key}
                    </code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(lastGeneratedKey.key);
                        alert("Copied!");
                      }}
                      className="p-2 text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition-colors"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setLastGeneratedKey(null)}
                      className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Create Server Form */}
            <ServerCreatePanel newServer={newServer} onChange={setNewServer} onCreate={createServer} />

            {/* Server List */}
            <div>
              <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-white">Server inventory</h2>
                  <p className="mt-1 text-xs text-slate-500">Quick actions stay visible; advanced and destructive operations are grouped under Manage.</p>
                </div>
                <span className="rounded-md border border-slate-700 bg-slate-900 px-2.5 py-1 font-mono text-xs text-slate-400" data-testid="server-inventory-count">{servers.length} registered</span>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3" data-testid="server-inventory-grid">
                {servers.map(server => (
                  <ServerCard
                    key={server.id}
                    checkingHealth={checkingHealth === server.id || checkingHealth === "all"}
                    fingerproxySyncing={fingerproxySyncing === server.id}
                    health={serverHealth[server.id]}
                    isDomainsOpen={selectedServer === server.id}
                    isSetupOpen={setupDomainsServer === server.id}
                    onDelete={() => deleteServer(server.id)}
                    onHealth={() => checkServerHealth(server.id)}
                    onRebalance={() => openRebalanceHelper(server)}
                    onRegenerateKey={() => regenerateServerKey(server.id)}
                    onSyncTls={() => syncFingerproxy(server.id)}
                    onTestConnection={() => testServerConnection(server.id)}
                    onToggleDomains={() => setSelectedServer(selectedServer === server.id ? null : server.id)}
                    onToggleSetup={() => setSetupDomainsServer(setupDomainsServer === server.id ? null : server.id)}
                    onViewStats={() => fetchServerStats(server.id)}
                    server={server}
                    testingConnection={testingConnection === server.id}
                  >

                    {/* Connection Test Result */}
                    {connectionResult && connectionResult.serverId === server.id && (
                      <div className={`mt-4 p-3 rounded-lg border ${
                        connectionResult.success 
                          ? "bg-emerald-500/10 border-emerald-500/30" 
                          : "bg-red-500/10 border-red-500/30"
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {connectionResult.success ? (
                              <Check className="w-4 h-4 text-emerald-400" />
                            ) : (
                              <AlertTriangle className="w-4 h-4 text-red-400" />
                            )}
                            <span className={connectionResult.success ? "text-emerald-400" : "text-red-400"}>
                              {connectionResult.message}
                            </span>
                          </div>
                          <button
                            onClick={() => setConnectionResult(null)}
                            className="text-slate-400 hover:text-white"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        {connectionResult.details && (
                          <div className="mt-2 text-xs text-slate-400 space-y-1">
                            {connectionResult.details.domains_count !== undefined && (
                              <p>Domains: {connectionResult.details.domains_count} | Links: {connectionResult.details.links_count}</p>
                            )}
                            {connectionResult.details.last_sync && (
                              <p>Last sync: {new Date(connectionResult.details.last_sync).toLocaleString()}</p>
                            )}
                            {connectionResult.details.suggestion && (
                              <p className="text-amber-400">{connectionResult.details.suggestion}</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Domain Assignment Panel */}
                    {selectedServer === server.id && (
                      <div className="mt-4 pt-4 border-t border-slate-700/50">
                        {/* Add New Domain */}
                        <div className="mb-3">
                          {(server.domains || server.active_domains || []).length > 0 && (
                            <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-md border border-cyan-500/25 bg-cyan-500/5 p-3" data-testid={`domain-metadata-recovery-${server.id}`}>
                              <div>
                                <p className="text-xs font-semibold text-cyan-300">Deployment domains available</p>
                                <p className="mt-1 text-xs text-slate-400">Restore domains saved during deployment to the dashboard pool without changing the running agent.</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => repairServerDomainMetadata(server.id)}
                                disabled={repairingDomainMetadata === server.id}
                                className="btn-secondary flex items-center gap-2 text-xs disabled:opacity-50"
                                data-testid={`repair-domain-metadata-${server.id}`}
                              >
                                <RefreshCw className={`h-3.5 w-3.5 ${repairingDomainMetadata === server.id ? "animate-spin" : ""}`} />
                                {repairingDomainMetadata === server.id ? "Restoring..." : "Restore to pool"}
                              </button>
                            </div>
                          )}
                          {domainMetadataRepair?.serverId === server.id && (
                            <p className="mb-3 text-xs text-emerald-300" data-testid={`domain-metadata-recovery-result-${server.id}`}>
                              Restored {domainMetadataRepair.added.length} new, reassigned {domainMetadataRepair.reassigned.length}, unchanged {domainMetadataRepair.unchanged.length}.
                            </p>
                          )}
                          <div className="flex items-center gap-3">
                            <input
                              type="text"
                              placeholder="Enter new domain (e.g., sub.example.com)..."
                              value={newDomainInput}
                              onChange={(e) => setNewDomainInput(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && addDomainToServer(server.id)}
                              className="input-dark flex-1"
                              data-testid={`new-domain-input-${server.id}`}
                            />
                            <button
                              onClick={() => addDomainToServer(server.id)}
                              disabled={!newDomainInput.trim() || addDomainDeploying}
                              className="btn-primary"
                              data-testid={`add-domain-btn-${server.id}`}
                            >
                              + Add & Deploy
                            </button>
                          </div>

                          {/* SSH Password Prompt + Deploy Logs */}
                          {pendingDomainAdd && pendingDomainAdd.serverId === server.id && (
                            <div className="mt-3 p-4 bg-slate-800/80 border border-slate-600 rounded-lg">
                              <p className="text-sm text-cyan-400 mb-2 font-medium">
                                Deploy <span className="text-white">{pendingDomainAdd.domain}</span> → Nginx + SSL on {server.name || server.ip_address}
                              </p>
                              {!addDomainDeploying && !addDomainLogs && (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="password"
                                    placeholder="SSH root password..."
                                    value={addDomainSshPassword}
                                    onChange={(e) => setAddDomainSshPassword(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && confirmAddDomainToServer()}
                                    className="input-dark flex-1"
                                    data-testid="add-domain-ssh-password"
                                  />
                                  <button
                                    onClick={confirmAddDomainToServer}
                                    disabled={!addDomainSshPassword}
                                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
                                    data-testid="confirm-add-domain-btn"
                                  >
                                    Deploy
                                  </button>
                                  <button
                                    onClick={cancelAddDomain}
                                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-sm transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              )}
                              {addDomainLogs && (
                                <div className="mt-2 bg-black/50 rounded-lg p-3 max-h-48 overflow-y-auto font-mono text-xs space-y-0.5">
                                  {addDomainLogs.map((log, i) => (
                                    <div key={i} className={
                                      log.includes("[SUCCESS]") || log.includes("[DNS OK]") ? "text-green-400" :
                                      log.includes("[ERROR]") || log.includes("[DNS FAIL]") ? "text-red-400" :
                                      log.includes("[DNS WARNING]") || log.includes("[WARNING]") ? "text-amber-400" :
                                      log.includes("[INFO]") ? "text-cyan-400" :
                                      "text-slate-400"
                                    }>{log}</div>
                                  ))}
                                  {addDomainDeploying && <div className="text-amber-400 animate-pulse">Setting up... this may take a minute</div>}
                                </div>
                              )}
                              {addDomainLogs && !addDomainDeploying && (
                                <button
                                  onClick={cancelAddDomain}
                                  className="mt-2 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-sm transition-colors"
                                >
                                  Close
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        {/* Existing assign from pool */}
                        <div className="flex items-center gap-3 mb-3">
                          <select
                            value={serverDomainAssign.domain_id}
                            onChange={(e) => setServerDomainAssign({ server_id: server.id, domain_id: e.target.value })}
                            className="input-dark flex-1"
                          >
                            <option value="">Or assign existing domain from pool...</option>
                            {domains.filter(d => !d.server_id).map(d => (
                              <option key={d.id} value={d.id}>{d.domain}</option>
                            ))}
                          </select>
                          <button
                            onClick={assignDomainToServer}
                            disabled={!serverDomainAssign.domain_id}
                            className="btn-primary"
                          >
                            Assign
                          </button>
                        </div>
                        {/* Show domains linked to this server */}
                        <div className="flex flex-wrap gap-2">
                          {(server.pool_domains || []).map(pd => (
                            <span
                              key={pd.id}
                              className="flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 border border-purple-500/30 rounded-full text-sm text-purple-400"
                            >
                              {pd.domain}
                              <button
                                onClick={() => unassignDomainFromServer(server.id, pd.id)}
                                className="hover:text-red-400"
                                title="Permanently remove from server and shared pool"
                                aria-label={`Permanently remove ${pd.domain} from the server and shared pool`}
                                data-testid={`purge-server-domain-${pd.id}`}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                          {(!server.pool_domains || server.pool_domains.length === 0) && (
                            <span className="text-slate-500 text-sm">No domains assigned</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Setup Domains (Nginx + SSL) Panel */}
                    {setupDomainsServer === server.id && (
                      <div className="mt-4 pt-4 border-t border-lime-500/30">
                        <div className="flex items-center gap-2 mb-3">
                          <Shield className="w-5 h-5 text-lime-400" />
                          <span className="text-white font-medium">Setup Nginx + SSL for Domains</span>
                        </div>
                        <p className="text-xs text-slate-400 mb-3">Enter SSH password to configure Nginx reverse proxy and SSL certificates for all domains on this server.</p>
                        <div className="flex items-center gap-3">
                          <input
                            type="password"
                            placeholder="SSH root password..."
                            value={setupSshPassword}
                            onChange={(e) => setSetupSshPassword(e.target.value)}
                            className="input-dark flex-1"
                            data-testid={`setup-ssh-password-${server.id}`}
                          />
                          <button
                            onClick={() => {
                              const serverDomains = (server.pool_domains || []).map(pd => pd.domain);
                              if (serverDomains.length === 0) { alert("No domains assigned to this server"); return; }
                              setupDomainsOnServer(server.id, serverDomains);
                            }}
                            disabled={setupDomainsLoading || !setupSshPassword}
                            className="btn-primary flex items-center gap-2"
                            data-testid={`run-setup-domains-${server.id}`}
                          >
                            {setupDomainsLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                            {setupDomainsLoading ? "Setting up..." : "Setup All Domains"}
                          </button>
                        </div>
                        {setupDomainsResult && (
                          <div className={`mt-3 p-3 rounded-lg border text-xs font-mono max-h-40 overflow-y-auto ${
                            setupDomainsResult.status === "success" ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : "bg-red-500/10 border-red-500/30 text-red-300"
                          }`}>
                            {setupDomainsResult.logs?.map((log, i) => <div key={i}>{log}</div>)}
                          </div>
                        )}
                      </div>
                    )}
                  </ServerCard>
                ))}
                {servers.length === 0 && (
                  <div className="rounded-lg border border-dashed border-slate-700 bg-slate-900/40 py-12 text-center md:col-span-2 2xl:col-span-3" data-testid="server-inventory-empty-state">
                    <Server className="mx-auto h-8 w-8 text-slate-600" />
                    <p className="mt-3 text-sm font-medium text-slate-300">No redirect servers configured</p>
                    <p className="mt-1 text-xs text-slate-500">Register a server above to begin deployment.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Server Stats Modal */}
            {showServerStats && serverStats && (
              <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowServerStats(null)}>
                <div 
                  className="bg-slate-900 border border-slate-700 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
                  onClick={e => e.stopPropagation()}
                  data-testid="server-stats-modal"
                >
                  <div className="p-6 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-slate-900">
                    <div>
                      <h2 className="text-xl font-semibold text-white">{serverStats.server_name} - Statistics</h2>
                      <p className="text-sm text-slate-400 mt-1">Traffic and performance metrics</p>
                    </div>
                    <button onClick={() => setShowServerStats(null)} className="text-slate-400 hover:text-white">
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                  
                  <div className="p-6 space-y-6">
                    {/* Request Counts */}
                    <div className="grid grid-cols-4 gap-4">
                      {[
                        { label: "Last Hour", value: serverStats.requests?.last_hour || 0, color: "cyan" },
                        { label: "Last 24h", value: serverStats.requests?.last_24h || 0, color: "blue" },
                        { label: "Last 7 Days", value: serverStats.requests?.last_7d || 0, color: "purple" },
                        { label: "Total", value: serverStats.requests?.total || 0, color: "emerald" }
                      ].map(stat => (
                        <div key={stat.label} className={`p-4 rounded-lg bg-${stat.color}-500/10 border border-${stat.color}-500/30`}>
                          <p className="text-xs text-slate-400">{stat.label}</p>
                          <p className={`text-2xl font-bold text-${stat.color}-400 mt-1`}>{stat.value.toLocaleString()}</p>
                        </div>
                      ))}
                    </div>

                    {/* Status Breakdown */}
                    {serverStats.status_breakdown?.length > 0 && (
                      <div className="card-dark p-4">
                        <h3 className="font-medium text-white mb-3">Request Status (24h)</h3>
                        <div className="space-y-2">
                          {serverStats.status_breakdown.map(status => (
                            <div key={status.status} className="flex items-center justify-between">
                              <span className={`text-sm ${
                                status.status === "REDIRECTED" ? "text-emerald-400" :
                                status.status.includes("BLOCKED") ? "text-red-400" :
                                status.status === "LANDING_PAGE" ? "text-blue-400" :
                                "text-slate-400"
                              }`}>{status.status}</span>
                              <div className="flex items-center gap-2">
                                <div className="w-32 h-2 bg-slate-700 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full ${
                                      status.status === "REDIRECTED" ? "bg-emerald-500" :
                                      status.status.includes("BLOCKED") ? "bg-red-500" :
                                      "bg-blue-500"
                                    }`}
                                    style={{ width: `${Math.min(100, (status.count / (serverStats.requests?.last_24h || 1)) * 100)}%` }}
                                  />
                                </div>
                                <span className="text-sm text-slate-400 w-16 text-right">{status.count}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Top Domains & Device Breakdown */}
                    <div className="grid grid-cols-2 gap-4">
                      {serverStats.top_domains?.length > 0 && (
                        <div className="card-dark p-4">
                          <h3 className="font-medium text-white mb-3">Top Domains (24h)</h3>
                          <div className="space-y-2">
                            {serverStats.top_domains.slice(0, 5).map((d, i) => (
                              <div key={d.domain} className="flex items-center justify-between">
                                <span className="text-sm text-slate-300">{i + 1}. {d.domain}</span>
                                <span className="text-sm text-cyan-400">{d.count}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {serverStats.device_breakdown?.length > 0 && (
                        <div className="card-dark p-4">
                          <h3 className="font-medium text-white mb-3">Devices (24h)</h3>
                          <div className="space-y-2">
                            {serverStats.device_breakdown.map(d => (
                              <div key={d.device} className="flex items-center justify-between">
                                <span className="text-sm text-slate-300 capitalize">{d.device || "unknown"}</span>
                                <span className="text-sm text-purple-400">{d.count}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Deployment Wizard Tab */}
        {activeTab === "deployment" && (
          <div className="admin-section" data-testid="deployment-tab">
            <header className="flex flex-col gap-4 border-b border-zinc-800 pb-5 xl:flex-row xl:items-end xl:justify-between">
              <div>
                <p className="text-xs font-bold uppercase text-indigo-300">Agent provisioning</p>
                <h1 className="mt-1 font-orbitron text-3xl font-bold text-zinc-50" data-testid="deployment-title">Deployment Wizard</h1>
                <p className="mt-2 text-sm text-zinc-500">Generate, execute, and verify installation plans for redirect agents.</p>
              </div>
              {deploymentStep > 1 && (
                <button
                  onClick={resetDeploymentWizard}
                  className="btn-secondary flex items-center gap-2"
                  data-testid="new-deployment-button"
                >
                  <Plus className="w-4 h-4" />
                  New Deployment
                </button>
              )}
            </header>

            {/* Step Indicator */}
            <div className="grid grid-cols-2 gap-px overflow-hidden border border-zinc-800 bg-zinc-800 md:grid-cols-5" data-testid="deployment-stepper">
              {[
                { step: 1, label: "Server Details" },
                { step: 2, label: "Install & Deploy" },
                { step: 3, label: "Manual Install" },
                { step: 4, label: "Deploying" },
                { step: 5, label: "Complete" }
              ].map((s, idx) => (
                <div key={s.step} className="flex items-center gap-2 bg-[#121215] px-3 py-3" data-testid={`deployment-step-${s.step}`}>
                  <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-colors ${
                    deploymentStep >= s.step
                      ? deploymentStep === s.step ? "bg-lime-500 text-black" : "bg-lime-500/50 text-black"
                      : "bg-slate-700 text-slate-400"
                  }`}>
                    {deploymentStep > s.step ? <Check className="w-4 h-4" /> : s.step}
                  </div>
                  <span className={`text-xs ${deploymentStep >= s.step ? "text-white" : "text-slate-500"}`}>
                    {s.label}
                  </span>
                  {idx < 4 && <ChevronRight className="ml-auto hidden h-3 w-3 text-zinc-700 md:block" />}
                </div>
              ))}
            </div>

            {/* Step 1: Server Details Form */}
            {deploymentStep === 1 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Server Info */}
                <div className="space-y-6">
                  <div className="card-dark p-5">
                    <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                      <Server className="w-5 h-5 text-lime-400" />
                      Server Information
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="text-xs text-slate-400 mb-1 block">Server Name *</label>
                        <input
                          type="text"
                          placeholder="e.g., US-East-1, COMCAST, OFF-Server"
                          value={deploymentForm.server_name}
                          onChange={(e) => setDeploymentForm({ ...deploymentForm, server_name: e.target.value })}
                          className="input-dark w-full"
                          data-testid="deploy-server-name"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 mb-1 block">Server IP Address *</label>
                        <input
                          type="text"
                          placeholder="e.g., 192.168.1.100"
                          value={deploymentForm.server_ip}
                          onChange={(e) => setDeploymentForm({ ...deploymentForm, server_ip: e.target.value })}
                          className="input-dark w-full"
                          data-testid="deploy-server-ip"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 mb-1 block">Dashboard API URL</label>
                        <input
                          type="text"
                          placeholder="https://wizard-x-routes.shop/api"
                          value={deploymentForm.dashboard_url}
                          onChange={(e) => setDeploymentForm({ ...deploymentForm, dashboard_url: e.target.value })}
                          className="input-dark w-full"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 mb-1 block">GitHub Repo URL (for auto-cloning agent code)</label>
                        <input
                          type="text"
                          placeholder="https://github.com/user/repo.git"
                          value={deploymentForm.github_repo_url}
                          onChange={(e) => setDeploymentForm({ ...deploymentForm, github_repo_url: e.target.value })}
                          className="input-dark w-full font-mono text-sm"
                          data-testid="deploy-github-repo"
                        />
                        <p className="text-xs text-slate-500 mt-1">Private repo? Use: https://TOKEN@github.com/user/repo.git</p>
                      </div>
                      <div className="md:col-span-2 pt-2 border-t border-slate-800">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-semibold text-slate-300 block flex items-center gap-2">
                            MaxMind Official Credentials (GeoIP)
                          </label>
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              try {
                                await axios.post(`${API}/admin/deployment/defaults`, {
                                  maxmind_account_id: deploymentForm.maxmind_account_id,
                                  maxmind_license_key: deploymentForm.maxmind_license_key
                                }, { headers });
                                alert("MaxMind credentials saved globally!");
                              } catch (err) {
                                alert("Error saving credentials");
                              }
                            }}
                            className="px-3 py-1 bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 rounded border border-cyan-500/30 text-xs font-medium transition-colors"
                          >
                            Save Globally
                          </button>
                        </div>
                        <p className="text-xs text-slate-500 mb-3">If provided, deployments will securely install the official MaxMind GeoLite2 databases via geoipupdate instead of using mirrors.</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs text-slate-400 mb-1 block">Account ID</label>
                            <input
                              type="text"
                              placeholder="e.g. 123456"
                              value={deploymentForm.maxmind_account_id}
                              onChange={(e) => setDeploymentForm({ ...deploymentForm, maxmind_account_id: e.target.value })}
                              className="input-dark w-full font-mono text-sm"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-slate-400 mb-1 block">License Key</label>
                            <input
                              type="password"
                              placeholder="Your MaxMind License Key"
                              value={deploymentForm.maxmind_license_key}
                              onChange={(e) => setDeploymentForm({ ...deploymentForm, maxmind_license_key: e.target.value })}
                              className="input-dark w-full font-mono text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="card-dark p-5">
                    <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                      <Globe className="w-5 h-5 text-purple-400" />
                      Domains (one per line or comma-separated)
                    </h3>
                    <textarea
                      placeholder="domain1.com&#10;domain2.com&#10;subdomain.domain3.com"
                      value={deploymentForm.domains}
                      onChange={(e) => setDeploymentForm({ ...deploymentForm, domains: e.target.value })}
                      className="input-dark w-full h-32 resize-none font-mono text-sm"
                      data-testid="deploy-domains"
                    />
                    <p className="text-xs text-slate-500 mt-2">
                      These domains will be configured in Nginx and tenants.json
                    </p>
                    <label className="flex items-start gap-2 mt-4 cursor-pointer group" data-testid="deploy-register-in-pool-toggle">
                      <input
                        type="checkbox"
                        checked={deploymentForm.register_in_pool}
                        onChange={(e) => setDeploymentForm({ ...deploymentForm, register_in_pool: e.target.checked })}
                        className="mt-0.5 w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 cursor-pointer flex-shrink-0"
                      />
                      <span className="text-xs text-slate-400 group-hover:text-slate-300 leading-relaxed">
                        <span className="text-white font-medium">Register in Domain Pool</span> — Make these domains immediately available for user-assignment.
                        <span className="block text-slate-500 mt-0.5">
                          Leave unchecked if the domains should stay agent-only and not pollute the Pool. You can add them manually later.
                        </span>
                      </span>
                    </label>
                  </div>
                </div>

                {/* Right Column - Configuration */}
                <div className="space-y-6">
                  <div className="card-dark p-5">
                    <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-cyan-400" />
                      Redirect Configuration
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="text-xs text-slate-400 mb-1 block">Default User ID</label>
                        <input
                          type="text"
                          placeholder="User ID for visitor logging"
                          value={deploymentForm.user_id}
                          onChange={(e) => setDeploymentForm({ ...deploymentForm, user_id: e.target.value })}
                          className="input-dark w-full"
                        />
                        <p className="text-xs text-slate-500 mt-1">Leave empty if using multi-tenant with tenants.json</p>
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 mb-1 block">Redirect URL Template</label>
                        <input
                          type="text"
                          placeholder="https://destination.com/path#[EMAIL]"
                          value={deploymentForm.redirect_url}
                          onChange={(e) => setDeploymentForm({ ...deploymentForm, redirect_url: e.target.value })}
                          className="input-dark w-full font-mono text-sm"
                        />
                        <p className="text-xs text-slate-500 mt-1">Use [EMAIL] or [EMAIL64] placeholders</p>
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 mb-1 block">Landing Page Template</label>
                        <Select value={deploymentForm.landing_page} onValueChange={(value) => setDeploymentForm({ ...deploymentForm, landing_page: value })}>
                          <SelectTrigger className="input-dark w-full" data-testid="deployment-landing-template-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {landingPageTemplates.map((template, index) => {
                              const value = template.id || template.value || `template-${index}`;
                              const name = template.name || template.label || value;
                              return <SelectItem key={value} value={value}>{name}{template.description ? ` — ${template.description}` : ""}</SelectItem>;
                            })}
                            {landingPageTemplates.length === 0 && <><SelectItem value="cloudflare">Cloudflare — Turnstile verification</SelectItem><SelectItem value="outlook">Outlook — Email preview</SelectItem><SelectItem value="teams">Teams — File sharing</SelectItem><SelectItem value="sharepoint">SharePoint — Document</SelectItem><SelectItem value="docusign">DocuSign — Document view</SelectItem></>}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <div className="card-dark p-5">
                    <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                      <Bell className="w-5 h-5 text-blue-400" />
                      Telegram Alerts (Optional)
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-white">Enable Telegram Alerts</label>
                        <button
                          type="button"
                          onClick={() => setDeploymentForm({ ...deploymentForm, enable_telegram: !deploymentForm.enable_telegram })}
                          className={`w-12 h-6 rounded-full transition-colors relative ${
                            deploymentForm.enable_telegram ? 'bg-blue-500' : 'bg-slate-600'
                          }`}
                        >
                          <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                            deploymentForm.enable_telegram ? 'translate-x-7' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>
                      {deploymentForm.enable_telegram && (
                        <>
                          <div>
                            <label className="text-xs text-slate-400 mb-1 block">Bot Token</label>
                            <input
                              type="text"
                              placeholder="123456789:ABC..."
                              value={deploymentForm.telegram_bot_token}
                              onChange={(e) => setDeploymentForm({ ...deploymentForm, telegram_bot_token: e.target.value })}
                              className="input-dark w-full font-mono text-sm"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-slate-400 mb-1 block">Chat ID</label>
                            <input
                              type="text"
                              placeholder="-1001234567890"
                              value={deploymentForm.telegram_chat_id}
                              onChange={(e) => setDeploymentForm({ ...deploymentForm, telegram_chat_id: e.target.value })}
                              className="input-dark w-full font-mono text-sm"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Generate Button */}
                <div className="lg:col-span-2">
                  <button
                    onClick={generateDeploymentScript}
                    disabled={!deploymentForm.server_name || !deploymentForm.server_ip || generatingScript}
                    className="btn-primary w-full py-4 text-lg flex items-center justify-center gap-3 disabled:opacity-50"
                    data-testid="generate-script-btn"
                  >
                    {generatingScript ? (
                      <>
                        <RefreshCw className="w-5 h-5 animate-spin" />
                        Generating Script...
                      </>
                    ) : (
                      <>
                        <Zap className="w-5 h-5" />
                        Generate Deployment Script
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Generated Script */}
            {deploymentStep === 2 && generatedScript && (
              <div className="space-y-6">
                <div
                  className="border border-amber-500/30 bg-amber-500/10 p-4 rounded-lg flex items-start gap-3"
                  data-testid="deployment-registration-pending-status"
                >
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-300">Registration created — installation is still required</p>
                    <p className="text-xs text-slate-400 mt-1">
                      Generating this script only creates the dashboard record. {deploymentForm.server_name || "The new server"} stays offline until Deploy Now succeeds or you run the script manually and its verified heartbeat is accepted.
                    </p>
                  </div>
                </div>
                <DeploymentCredentialsPanel apiKey={generatedApiKey} agentAdmin={generatedAgentAdmin} />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column: Script */}
                  <div className="card-dark p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-white flex items-center gap-2">
                        <FileText className="w-5 h-5 text-lime-400" />
                        Generated Install Script
                      </h3>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(generatedScript);
                            alert("Script copied to clipboard!");
                          }}
                          className="btn-secondary flex items-center gap-2 text-xs"
                        >
                          <Copy className="w-3 h-3" />
                          Copy
                        </button>
                        <button
                          onClick={() => {
                            const blob = new Blob([generatedScript], { type: "text/plain" });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `install-${deploymentForm.server_name.toLowerCase().replace(/\s+/g, "-")}.sh`;
                            a.click();
                            URL.revokeObjectURL(url);
                          }}
                          className="btn-primary flex items-center gap-2 text-xs"
                        >
                          <Download className="w-3 h-3" />
                          Download
                        </button>
                      </div>
                    </div>
                    <div className="bg-slate-950 border border-slate-800 rounded-lg overflow-hidden">
                      <div className="bg-slate-900 px-4 py-2 border-b border-slate-800 flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        <span className="ml-2 text-xs text-slate-500 font-mono">install-{deploymentForm.server_name.toLowerCase().replace(/\s+/g, "-")}.sh</span>
                      </div>
                      <pre className="p-4 text-xs text-slate-300 font-mono overflow-x-auto max-h-64 whitespace-pre">
                        {generatedScript}
                      </pre>
                    </div>
                  </div>

                  {/* Right Column: Remote Deployment */}
                  <div className="space-y-4">
                    <div className="card-dark p-5 border border-lime-500/30 bg-lime-500/5">
                      <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                        <Zap className="w-5 h-5 text-lime-400" />
                        Deploy Now (Automated)
                      </h3>
                      <p className="text-sm text-slate-400 mb-4">
                        Enter SSH credentials to deploy automatically. We'll connect to your server and run the script for you.
                      </p>
                      
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-slate-400 mb-1 block">SSH User</label>
                            <input
                              type="text"
                              value={sshCredentials.ssh_user}
                              onChange={(e) => setSshCredentials({ ...sshCredentials, ssh_user: e.target.value })}
                              className="input-dark w-full"
                              placeholder="root"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-slate-400 mb-1 block">SSH Port</label>
                            <input
                              type="number"
                              value={sshCredentials.ssh_port}
                              onChange={(e) => setSshCredentials({ ...sshCredentials, ssh_port: parseInt(e.target.value) })}
                              className="input-dark w-full"
                              placeholder="22"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-slate-400 mb-1 block">SSH Password *</label>
                          <input
                            type="password"
                            value={sshCredentials.ssh_password}
                            onChange={(e) => {
                              setSshCredentials({ ...sshCredentials, ssh_password: e.target.value });
                              setConnectionTested(false);
                            }}
                            className="input-dark w-full"
                            placeholder="Enter server password"
                          />
                        </div>
                        
                        <div className="flex gap-2">
                          <button
                            onClick={testSshConnection}
                            disabled={deploymentStatus === "testing" || !sshCredentials.ssh_password}
                            className="btn-secondary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
                          >
                            {deploymentStatus === "testing" ? (
                              <>
                                <RefreshCw className="w-4 h-4 animate-spin" />
                                Testing...
                              </>
                            ) : connectionTested ? (
                              <>
                                <CheckCircle className="w-4 h-4 text-green-400" />
                                Connected
                              </>
                            ) : (
                              <>
                                <Server className="w-4 h-4" />
                                Test Connection
                              </>
                            )}
                          </button>
                          <button
                            onClick={deployRemotely}
                            disabled={!connectionTested || isDeploying}
                            className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
                          >
                            <Zap className="w-4 h-4" />
                            Deploy Now
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Deployment Logs */}
                    {deploymentLogs.length > 0 && (
                      <div className="card-dark p-4">
                        <h4 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                          <Activity className="w-4 h-4 text-cyan-400" />
                          Connection Log
                        </h4>
                        <div className="bg-slate-950 border border-slate-800 rounded-lg p-3 max-h-40 overflow-y-auto font-mono text-xs">
                          {deploymentLogs.map((log, idx) => (
                            <div key={idx} className={`${
                              log.includes("[ERROR]") ? "text-red-400" : 
                              log.includes("[SUCCESS]") ? "text-green-400" : 
                              log.includes("[WARNING]") ? "text-yellow-400" : 
                              "text-slate-400"
                            }`}>
                              {log}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={() => setDeploymentStep(3)}
                    className="btn-secondary flex-1 py-3 flex items-center justify-center gap-2"
                  >
                    Manual Deployment Steps
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Deploying */}
            {deploymentStep === 4 && (
              <div className="space-y-6">
                <div className="card-dark p-6 text-center">
                  <div className="w-20 h-20 mx-auto rounded-full bg-lime-500/20 flex items-center justify-center mb-4">
                    {deploymentStatus === "success" ? (
                      <CheckCircle className="w-10 h-10 text-lime-400" />
                    ) : deploymentStatus === "failed" ? (
                      <XCircle className="w-10 h-10 text-red-400" />
                    ) : (
                      <RefreshCw className="w-10 h-10 text-lime-400 animate-spin" />
                    )}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {deploymentStatus === "success" ? "Deployment Complete!" : 
                     deploymentStatus === "failed" ? "Deployment Failed" : 
                     "Deploying to Server..."}
                  </h3>
                  <p className="text-slate-400 text-sm mb-6">
                    {deploymentStatus === "success" ? "Health, bot defense, and the first dashboard heartbeat were verified." :
                     deploymentStatus === "failed" ? "There was an error during deployment. Check the logs below." :
                     "Please wait while we install dependencies and configure your server."}
                  </p>
                </div>

                {/* Live Deployment Logs */}
                <div className="card-dark p-5">
                  <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-cyan-400" />
                    Deployment Logs
                  </h3>
                  <div className="bg-slate-950 border border-slate-800 rounded-lg p-4 max-h-96 overflow-y-auto font-mono text-sm">
                    {deploymentLogs.map((log, idx) => (
                      <div key={idx} className={`mb-1 ${
                        log.includes("[ERROR]") ? "text-red-400" : 
                        log.includes("[SUCCESS]") ? "text-green-400" : 
                        log.includes("[WARNING]") ? "text-yellow-400" :
                        log.includes("[OUTPUT]") ? "text-slate-300" :
                        "text-cyan-400"
                      }`}>
                        {log}
                      </div>
                    ))}
                    {isDeploying && (
                      <div className="text-lime-400 animate-pulse">▋</div>
                    )}
                  </div>
                </div>

                {deploymentStatus === "success" && (
                  <div className="flex gap-4">
                    <button
                      onClick={copyFilesFromSource}
                      className="btn-secondary flex-1 py-3 flex items-center justify-center gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      Copy Files from COMCAST
                    </button>
                    <button
                      onClick={() => setDeploymentStep(5)}
                      className="btn-primary flex-1 py-3 flex items-center justify-center gap-2"
                    >
                      Continue
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {deploymentStatus === "failed" && (
                  <div className="flex gap-4">
                    <button
                      onClick={() => setDeploymentStep(2)}
                      className="btn-secondary flex-1 py-3 flex items-center justify-center gap-2"
                    >
                      <ChevronRight className="w-4 h-4 rotate-180" />
                      Back to Script
                    </button>
                    <button
                      onClick={() => {
                        setDeploymentLogs([]);
                        deployRemotely();
                      }}
                      className="btn-primary flex-1 py-3 flex items-center justify-center gap-2"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Retry Deployment
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Step 5: Completion */}
            {deploymentStep === 5 && (
              <div className="space-y-6">
                <div className="card-dark p-6 text-center border border-lime-500/30 bg-lime-500/5">
                  <div className="w-20 h-20 mx-auto rounded-full bg-lime-500/20 flex items-center justify-center mb-4">
                    <CheckCircle className="w-10 h-10 text-lime-400" />
                  </div>
                  <h3 className="text-2xl font-semibold text-white mb-2">
                    Server Deployed Successfully!
                  </h3>
                  <p className="text-slate-400 mb-4">
                    {deploymentForm.server_name} ({deploymentForm.server_ip}) is now set up.
                  </p>
                </div>

                {/* SSL Status */}
                <div className="card-dark p-5 border border-teal-500/30 bg-teal-500/5">
                  <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Lock className="w-5 h-5 text-teal-400" />
                    SSL Certificates
                  </h3>
                  <p className="text-sm text-slate-400 mb-3">
                    SSL provisioning was attempted via Let's Encrypt for the configured domains. Verify each domain after DNS points to this server.
                  </p>
                  <div className="space-y-2 mb-4">
                    {deploymentForm.domains.split(/[,\n]/).filter(d => d.trim()).map((domain, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <Activity className="w-4 h-4 text-teal-400" />
                        <span className="text-white font-mono">{domain.trim()}</span>
                        <span className="text-slate-500 text-xs">- verification required</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500">
                    If any domain failed SSL (e.g., DNS not yet pointed), you can re-provision from the Redirect Servers tab by assigning the domain.
                  </p>
                </div>

                <div className="card-dark p-5">
                  <h3 className="font-semibold text-white mb-4">Post-Deployment Checklist</h3>
                  <div className="space-y-4">
                    <div className="flex gap-4 items-start">
                      <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-cyan-400 font-bold text-sm">1</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-white">Copy app.py and templates</h4>
                        <p className="text-sm text-slate-400 mb-2">If not already done, copy the files from COMCAST server:</p>
                        <button
                          onClick={copyFilesFromSource}
                          disabled={deploymentStatus === "copying"}
                          className="btn-secondary text-sm flex items-center gap-2"
                        >
                          {deploymentStatus === "copying" ? (
                            <>
                              <RefreshCw className="w-3 h-3 animate-spin" />
                              Copying...
                            </>
                          ) : (
                            <>
                              <Upload className="w-3 h-3" />
                              Copy Files Now
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="flex gap-4 items-start">
                      <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-cyan-400 font-bold text-sm">2</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-white">Verify DNS Records</h4>
                        <p className="text-sm text-slate-400">Ensure all domains point to <code className="text-cyan-400">{deploymentForm.server_ip}</code> via Cloudflare or your DNS provider.</p>
                      </div>
                    </div>
                    <div className="flex gap-4 items-start">
                      <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-cyan-400 font-bold text-sm">3</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-white">Trigger Config Sync</h4>
                        <p className="text-sm text-slate-400">Go to Config Sync tab and trigger a sync to push tenant configs to this new server.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Logs if any */}
                {deploymentLogs.length > 0 && (
                  <div className="card-dark p-4">
                    <h4 className="text-sm font-semibold text-white mb-2">Latest Logs</h4>
                    <div className="bg-slate-950 border border-slate-800 rounded-lg p-3 max-h-32 overflow-y-auto font-mono text-xs">
                      {deploymentLogs.slice(-10).map((log, idx) => (
                        <div key={idx} className={`${
                          log.includes("[ERROR]") ? "text-red-400" : 
                          log.includes("[SUCCESS]") ? "text-green-400" : 
                          "text-slate-400"
                        }`}>
                          {log}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={resetDeploymentWizard}
                  className="btn-primary w-full py-3 flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Deploy Another Server
                </button>
              </div>
            )}

            {/* Step 3: Final Instructions */}
            {deploymentStep === 3 && (
              <div className="space-y-6">
                <div className="card-dark p-6">
                  <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-lime-400" />
                    Deployment Instructions
                  </h3>
                  
                  <div className="space-y-6">
                    {/* Step 1 */}
                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-cyan-400 font-bold">1</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-2">SSH into your new server</h4>
                        <code className="block bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 font-mono text-sm text-cyan-400">
                          ssh root@{deploymentForm.server_ip}
                        </code>
                      </div>
                    </div>

                    {/* Step 2 */}
                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-cyan-400 font-bold">2</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-2">Upload and run the install script</h4>
                        <div className="space-y-2">
                          <code className="block bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 font-mono text-sm text-cyan-400">
                            # Option A: Paste script directly<br/>
                            nano install.sh  # Paste script, save with Ctrl+O, exit with Ctrl+X
                          </code>
                          <code className="block bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 font-mono text-sm text-cyan-400">
                            # Option B: Use SCP from your machine<br/>
                            scp install-{deploymentForm.server_name.toLowerCase().replace(/\s+/g, "-")}.sh root@{deploymentForm.server_ip}:/root/
                          </code>
                          <code className="block bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 font-mono text-sm text-lime-400">
                            chmod +x install.sh && ./install.sh
                          </code>
                        </div>
                      </div>
                    </div>

                    {/* Step 3 */}
                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-cyan-400 font-bold">3</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-2">Copy app.py and templates from COMCAST server</h4>
                        <code className="block bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 font-mono text-sm text-cyan-400">
                          # Run these from COMCAST server (209.38.141.226)<br/>
                          scp /opt/wizardx-agent/app.py root@{deploymentForm.server_ip}:/opt/wizardx-agent/<br/>
                          scp /opt/wizardx-agent/security_module.py root@{deploymentForm.server_ip}:/opt/wizardx-agent/<br/>
                          scp /root/Redirector/var/www/landing/path/*.html root@{deploymentForm.server_ip}:/root/Redirector/var/www/landing/path/
                        </code>
                      </div>
                    </div>

                    {/* Step 4 */}
                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-cyan-400 font-bold">4</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-2">Get SSL certificates</h4>
                        <code className="block bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 font-mono text-sm text-cyan-400">
                          certbot --nginx -d {deploymentForm.domains.split(/[,\n]/).filter(d => d.trim()).join(" -d ") || "YOUR-DOMAIN.com"}
                        </code>
                      </div>
                    </div>

                    {/* Step 5 */}
                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-cyan-400 font-bold">5</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-2">Start and verify</h4>
                        <code className="block bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 font-mono text-sm text-lime-400">
                          systemctl restart redirector nginx<br/>
                          curl http://127.0.0.1:5000/api/health
                        </code>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg" data-testid="manual-deployment-pending-status">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="w-6 h-6 text-amber-400" />
                      <div>
                        <p className="text-amber-300 font-semibold">Registered, but not deployed yet</p>
                        <p className="text-sm text-slate-400">Server "{deploymentForm.server_name}" remains offline until the install script finishes its health checks and sends the first verified heartbeat.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={() => setDeploymentStep(2)}
                    className="btn-secondary flex-1 flex items-center justify-center gap-2"
                  >
                    <ChevronRight className="w-4 h-4 rotate-180" />
                    Back to Script
                  </button>
                  <button
                    onClick={resetDeploymentWizard}
                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Deploy Another Server
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "domains" && (
          <div data-testid="domain-pool-section"><DomainPoolWorkspace
            api={API}
            headers={headers}
            domains={domains}
            groups={domainGroups}
            users={users}
            customDomains={customDomainsList}
            customBusyId={cdBusy}
            form={{ domain: newDomain, setDomain: setNewDomain, group: newDomainGroup, setGroup: setNewDomainGroup, rating: newDomainRating, setRating: setNewDomainRating, tags: newDomainTags, setTags: setNewDomainTags, notes: newDomainNotes, setNotes: setNewDomainNotes }}
            groupForm={{ name: newGroupName, description: newGroupDesc, color: newGroupColor, onNameChange: setNewGroupName, onDescriptionChange: setNewGroupDesc, onColorChange: setNewGroupColor, onCreate: addDomainGroup, onDelete: deleteDomainGroup }}
            controls={{ search: domainSearch, filter: domainFilter, selectedIds: selectedDomainIds, showGroups: showGroupForm, healthLoading: healthCheckLoading, healthResults, bulkDeleteLoading, editingId: editingDomain, quickAssignId: quickAssignDomain, quickAssignUser, deleteConfirmId: deleteDomainConfirmId }}
            actions={{ setSearch: setDomainSearch, setFilter: setDomainFilter, setSelectedIds: setSelectedDomainIds, setShowGroups: setShowGroupForm, setHealthResults, healthCheck: checkDomainsHealth, bulkDelete: bulkDeleteDomains, toggleSelection: toggleDomainSelection, toggleSelectAll: toggleSelectAllDomains, refreshCustom: fetchCustomDomains, retryCustom: retryCustomDomainSSL, revokeCustom: revokeCustomDomain, addDomain, copy: (value) => navigator.clipboard.writeText(value), setEditingId: setEditingDomain, updateDomain, setQuickAssignId: setQuickAssignDomain, setQuickAssignUser, quickAssign: quickAssignDomainToUser, requestDelete: requestDomainDelete, removeDomain, setDeleteConfirmId: setDeleteDomainConfirmId, requestUnassign: async (domain, assignedUser) => { if (await showConfirm("Unassign Domain", `Return ${domain.domain} to the available pool and remove ${assignedUser.username}'s links on it?`)) unassignDomain(assignedUser.assignment_id); } }}
          /></div>
        )}

        {/* Domain Assignments Tab */}
        {activeTab === "assignments" && (
          <div data-testid="domain-assignments-section"><DomainAssignmentWorkspace
            api={API}
            headers={headers}
            syncResult={fingerproxyResult}
            syncing={fingerproxySyncing === "all"}
            onSyncAll={() => syncFingerproxy()}
          /></div>
        )}

        {/* Legacy assignment markup retained temporarily for behavior-reference during modular migration. */}
        {false && activeTab === "assignments" && (
          <div className="admin-section" data-testid="legacy-domain-assignments-section">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-orbitron font-bold text-white" data-testid="assignments-title">Domain Assignments</h1>
                <p className="text-slate-400 text-sm mt-1">Assign domains to licensed users</p>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
                  <span className="text-slate-400">Total: </span>
                  <span className="text-cyan-400 font-semibold">{assignments.length}</span>
                </div>
                <div className="px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                  <span className="text-slate-400">Users: </span>
                  <span className="text-emerald-400 font-semibold">{[...new Set(assignments.map(a => a.username))].length}</span>
                </div>
              </div>
            </div>

            {/* Assignment Mode Tabs */}
            <div className="flex gap-2">
              <button
                onClick={() => setAssignMode("single")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  assignMode === "single" 
                    ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30" 
                    : "bg-slate-800 text-slate-400 border border-slate-700 hover:border-slate-600"
                }`}
              >
                Single Domain
              </button>
              <button
                onClick={() => setAssignMode("group")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  assignMode === "group" 
                    ? "bg-purple-500/20 text-purple-400 border border-purple-500/30" 
                    : "bg-slate-800 text-slate-400 border border-slate-700 hover:border-slate-600"
                }`}
              >
                <div className="flex items-center gap-2">
                  <FolderOpen className="w-4 h-4" />
                  From Group (Auto-Pick)
                </div>
              </button>
            </div>

            {/* Single Domain Assign Form */}
            {assignMode === "single" && (
              <div className="card-dark p-5">
                <h3 className="font-semibold text-white mb-4">Assign Single Domain</h3>
                <div className="flex gap-3 items-end">
                  <div className="flex-1">
                    <label className="text-xs text-slate-400 mb-1 block">Select User</label>
                    <select
                      value={selectedUser}
                      onChange={(e) => setSelectedUser(e.target.value)}
                      className="input-dark w-full"
                    >
                      <option value="">Choose a licensed user...</option>
                      {licensedUsers.map(u => (
                        <option key={u.id} value={u.id}>{u.username}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="text-xs text-slate-400 mb-1 block">Select Domain <span className="text-slate-600">(unassigned only)</span></label>
                    <select
                      value={selectedDomain}
                      onChange={(e) => setSelectedDomain(e.target.value)}
                      className="input-dark w-full"
                    >
                      <option value="">Choose a domain...</option>
                      {domainGroups.map(group => {
                        const unassigned = domains.filter(d => d.group_id === group.id && (!d.assigned_users || d.assigned_users.length === 0));
                        if (unassigned.length === 0) return null;
                        return (
                          <optgroup key={group.id} label={`📁 ${group.name} (${unassigned.length})`}>
                            {unassigned.map(d => (
                              <option key={d.id} value={d.id}>
                                {d.domain} {d.inbox_rating === "excellent" ? "⭐" : d.inbox_rating === "good" ? "✅" : d.inbox_rating === "poor" ? "⚠️" : ""}
                              </option>
                            ))}
                          </optgroup>
                        );
                      })}
                      {(() => {
                        const ungroupedUnassigned = domains.filter(d => !d.group_id && (!d.assigned_users || d.assigned_users.length === 0));
                        if (ungroupedUnassigned.length === 0) return null;
                        return (
                          <optgroup label={`📂 Ungrouped (${ungroupedUnassigned.length})`}>
                            {ungroupedUnassigned.map(d => (
                              <option key={d.id} value={d.id}>
                                {d.domain} {d.inbox_rating === "excellent" ? "⭐" : d.inbox_rating === "good" ? "✅" : d.inbox_rating === "poor" ? "⚠️" : ""}
                              </option>
                            ))}
                          </optgroup>
                        );
                      })()}
                    </select>
                  </div>
                  <button
                    onClick={assignDomain}
                    disabled={!selectedUser || !selectedDomain}
                    className="btn-primary flex items-center gap-2"
                  >
                    <Link2 className="w-4 h-4" />
                    Assign
                  </button>
                </div>
              </div>
            )}

            {/* Group Assignment Form */}
            {assignMode === "group" && (
              <div className="card-dark p-5 border-purple-500/20">
                <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                  <FolderOpen className="w-5 h-5 text-purple-400" />
                  Auto-Assign from Group
                </h3>
                <p className="text-xs text-slate-400 mb-4">
                  Automatically pick unassigned domains from a group and assign them to the user
                </p>
                <div className="flex gap-3 items-end flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-xs text-slate-400 mb-1 block">Select User</label>
                    <select
                      value={selectedUser}
                      onChange={(e) => setSelectedUser(e.target.value)}
                      className="input-dark w-full"
                    >
                      <option value="">Choose a licensed user...</option>
                      {licensedUsers.map(u => (
                        <option key={u.id} value={u.id}>{u.username} ({u.assigned_domains || 0} domains)</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-xs text-slate-400 mb-1 block">Select Domain Group</label>
                    <select
                      value={selectedGroupForAssign}
                      onChange={(e) => setSelectedGroupForAssign(e.target.value)}
                      className="input-dark w-full"
                    >
                      <option value="">Choose a group...</option>
                      {domainGroups.map(group => (
                        <option key={group.id} value={group.id}>
                          {group.name} ({group.domain_count || 0} domains)
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="w-24">
                    <label className="text-xs text-slate-400 mb-1 block">Count</label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={assignCount}
                      onChange={(e) => setAssignCount(Math.min(10, Math.max(1, parseInt(e.target.value) || 1)))}
                      className="input-dark w-full text-center"
                    />
                  </div>
                  <button
                    onClick={assignDomainsFromGroup}
                    disabled={!selectedUser || !selectedGroupForAssign}
                    className="btn-primary flex items-center gap-2 bg-purple-600 hover:bg-purple-500"
                  >
                    <Zap className="w-4 h-4" />
                    Auto-Assign {assignCount}
                  </button>
                </div>
                <p className="text-xs text-purple-300 mt-3">
                  The system will pick {assignCount} unassigned domain(s) from the selected group
                </p>
              </div>
            )}

            {/* Assignments List */}
            <div className="card-dark p-5">
              <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
                <h3 className="font-semibold text-white">Current Assignments ({assignments.length})</h3>
                <div className="relative w-72">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="text"
                    value={assignmentSearch}
                    onChange={(e) => setAssignmentSearch(e.target.value)}
                    placeholder="Search user or domain..."
                    className="input-dark w-full pl-9 text-sm"
                    data-testid="assignment-search"
                  />
                </div>
              </div>

              {/* User Filter Chips */}
              {(() => {
                const allUsers = [...new Set(assignments.map(a => a.username))].sort();
                if (allUsers.length > 1) {
                  return (
                    <div className="flex flex-wrap gap-2 mb-4">
                      <button
                        onClick={() => setAssignmentSearch("")}
                        className={`px-3 py-1 text-xs rounded-full transition-colors ${
                          !assignmentSearch ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30" : "bg-slate-800 text-slate-400 border border-slate-700 hover:border-slate-500"
                        }`}
                      >
                        All ({assignments.length})
                      </button>
                      {allUsers.map(u => {
                        const count = assignments.filter(a => a.username === u).length;
                        const isActive = assignmentSearch.toLowerCase() === u.toLowerCase();
                        return (
                          <button
                            key={u}
                            onClick={() => setAssignmentSearch(isActive ? "" : u)}
                            className={`px-3 py-1 text-xs rounded-full transition-colors ${
                              isActive ? "bg-blue-500/20 text-blue-400 border border-blue-500/30" : "bg-slate-800 text-slate-400 border border-slate-700 hover:border-slate-500"
                            }`}
                          >
                            {u} ({count})
                          </button>
                        );
                      })}
                    </div>
                  );
                }
                return null;
              })()}

              {/* Grouped by User */}
              {(() => {
                const search = assignmentSearch.toLowerCase();
                const filtered = assignments.filter(a =>
                  !search ||
                  a.username?.toLowerCase().includes(search) ||
                  a.domain?.toLowerCase().includes(search) ||
                  a.server_name?.toLowerCase().includes(search)
                );
                
                // Group by user
                const grouped = filtered.reduce((acc, a) => {
                  const key = a.username || "Unknown";
                  if (!acc[key]) acc[key] = [];
                  acc[key].push(a);
                  return acc;
                }, {});

                const usernames = Object.keys(grouped).sort();

                if (usernames.length === 0) {
                  return (
                    <p className="text-slate-500 text-center py-8" data-testid="no-assignments">
                      {assignmentSearch ? "No assignments match your search" : "No assignments yet"}
                    </p>
                  );
                }

                return (
                  <div className="space-y-4">
                    {usernames.map(username => (
                      <div key={username} className="border border-slate-700/50 rounded-xl overflow-hidden">
                        {/* User Header */}
                        <div className="flex items-center justify-between px-4 py-3 bg-slate-800/80">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                              <Users className="w-4 h-4 text-blue-400" />
                            </div>
                            <span className="text-white font-medium">{username}</span>
                            <span className="text-xs px-2 py-0.5 bg-slate-700 text-slate-300 rounded-full">
                              {grouped[username].length} domain{grouped[username].length !== 1 ? "s" : ""}
                            </span>
                          </div>
                          <button
                            onClick={async () => {
                              if (await showConfirm("Remove All Domains", `Remove all ${grouped[username].length} domain(s) from ${username}?`)) {
                                for (const a of grouped[username]) {
                                  try { await axios.delete(`${API}/admin/domains/assign/${a.id}`, { headers }); } catch {}
                                }
                                fetchAssignments(); fetchStats(); fetchDomains();
                              }
                            }}
                            className="px-3 py-1 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 border border-red-500/20 rounded-lg transition-colors"
                            data-testid={`bulk-unassign-${username}`}
                            title={`Remove all domains from ${username}`}
                          >
                            Remove All
                          </button>
                        </div>
                        {/* Domain Rows */}
                        <div className="divide-y divide-slate-800/50">
                          {grouped[username].map(assignment => (
                            <div key={assignment.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-800/30 transition-colors">
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <Globe className="w-4 h-4 text-purple-400 shrink-0" />
                                <span className="text-slate-300 text-sm truncate" title={assignment.domain}>{assignment.domain}</span>
                                {assignment.server_name && assignment.server_name !== "N/A" && (
                                  <span className="text-xs px-2 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded shrink-0">
                                    {assignment.server_name}
                                  </span>
                                )}
                                {assignment.assigned_at && (
                                  <span className="text-xs text-slate-600 shrink-0 hidden lg:inline">
                                    {new Date(assignment.assigned_at).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                              {deleteConfirmId === assignment.id ? (
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => unassignDomain(assignment.id)}
                                    className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded-lg transition-colors"
                                    data-testid={`confirm-unassign-${assignment.id}`}
                                  >
                                    Yes, Remove
                                  </button>
                                  <button
                                    onClick={() => setDeleteConfirmId(null)}
                                    className="px-3 py-1 bg-slate-600 hover:bg-slate-700 text-white text-xs rounded-lg transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setDeleteConfirmId(assignment.id)}
                                  className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                  data-testid={`unassign-domain-${assignment.id}`}
                                  title="Remove assignment"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Alias Paths Tab */}
        {activeTab === "alias-paths" && (
          <div className="admin-section" data-testid="alias-paths-section">
            <header className="flex flex-col gap-4 border-b border-zinc-800 pb-5 xl:flex-row xl:items-end xl:justify-between">
              <div>
                <p className="text-xs font-bold uppercase text-indigo-300">Routing configuration</p>
                <h1 className="mt-1 font-orbitron text-3xl font-bold text-zinc-50" data-testid="alias-paths-title">Alias Paths</h1>
                <p className="mt-2 text-sm text-zinc-500">Generate, group, assign, and synchronize reusable redirect paths.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={syncAliasPathsToServers} className="btn-secondary flex items-center gap-2" data-testid="sync-alias-paths-button">
                  <RefreshCw className="w-4 h-4" />
                  Sync to Servers
                </button>
                <button onClick={() => setShowPathGroupForm(true)} className="btn-primary flex items-center gap-2" data-testid="new-path-group-button">
                  <Plus className="w-4 h-4" />
                  New Group
                </button>
              </div>
            </header>
            <section className="grid grid-cols-2 gap-px overflow-hidden border border-zinc-800 bg-zinc-800 md:grid-cols-4" data-testid="alias-path-summary">{[["Total paths", aliasPaths.length, "text-zinc-100"], ["Path groups", pathGroups.length, "text-indigo-300"], ["Ungrouped", aliasPaths.filter((path) => !path.group_id).length, "text-amber-300"], ["User assignments", pathGroups.reduce((total, group) => total + Number(group.assigned_user_count || 0), 0), "text-emerald-300"]].map(([label, value, tone]) => <div key={label} className="bg-[#121215] p-4"><p className={`font-mono text-2xl font-bold ${tone}`}>{value}</p><p className="mt-1 text-[10px] uppercase text-zinc-600">{label}</p></div>)}</section>

            {/* Create Path Group Form */}
            {showPathGroupForm && (
              <div className="card-dark p-5 border border-orange-500/30">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <Tags className="w-5 h-5 text-orange-400" />
                  Create Path Group
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input
                    type="text"
                    placeholder="Group Name (e.g., Banking Paths)"
                    value={newPathGroup.name}
                    onChange={(e) => setNewPathGroup({ ...newPathGroup, name: e.target.value })}
                    className="input-dark"
                    data-testid="path-group-name-input"
                  />
                  <input
                    type="text"
                    placeholder="Description (optional)"
                    value={newPathGroup.description}
                    onChange={(e) => setNewPathGroup({ ...newPathGroup, description: e.target.value })}
                    className="input-dark"
                    data-testid="path-group-description-input"
                  />
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={newPathGroup.color}
                      onChange={(e) => setNewPathGroup({ ...newPathGroup, color: e.target.value })}
                      className="w-12 h-10 rounded border border-slate-600 cursor-pointer"
                      data-testid="path-group-color-input"
                    />
                    <button
                      onClick={createPathGroup}
                      disabled={!newPathGroup.name}
                      className="btn-primary flex-1 flex items-center justify-center gap-2"
                      data-testid="create-path-group-button"
                    >
                      <Check className="w-4 h-4" />
                      Create
                    </button>
                    <button
                      onClick={() => { setShowPathGroupForm(false); setNewPathGroup({ name: "", description: "", color: "#f97316" }); }}
                      className="btn-secondary"
                      data-testid="cancel-path-group-button"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Path Groups Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {pathGroups.map(group => (
                <div key={group.id} className="card-dark p-5 border-l-4" style={{ borderLeftColor: group.color }}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-white flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: group.color }}></div>
                        {group.name}
                      </h3>
                      <p className="text-slate-400 text-sm mt-1">{group.description || "No description"}</p>
                      <div className="flex gap-4 mt-2 text-xs text-slate-500">
                        <span>{group.path_count || 0} paths</span>
                        <span>{group.assigned_user_count || 0} users assigned</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openUserAssignModal(group.id)}
                        className="p-2 text-slate-400 hover:text-green-400 hover:bg-green-500/10 rounded-lg transition-colors"
                        title="Assign Users"
                      >
                        <UserCheck className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => viewGroupUsers(group.id)}
                        className="p-2 text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition-colors"
                        title="View Assigned Users"
                      >
                        <Users className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deletePathGroup(group.id)}
                        className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        title="Delete Group"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Add Path to Group */}
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      placeholder="/oauth2/v2.0/authorize..."
                      value={selectedPathGroup === group.id ? newPathForGroup : ""}
                      onFocus={() => setSelectedPathGroup(group.id)}
                      onChange={(e) => { setSelectedPathGroup(group.id); setNewPathForGroup(e.target.value); }}
                      className="input-dark flex-1 font-mono text-xs"
                    />
                    <button
                      onClick={() => addAliasPath(group.id)}
                      disabled={selectedPathGroup !== group.id || !newPathForGroup}
                      className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white text-xs rounded-lg transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Paths in Group */}
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {aliasPaths.filter(p => p.group_id === group.id).map(path => (
                      <div key={path.id} className="flex items-center justify-between p-2 bg-slate-800/50 rounded text-xs">
                        {editingPath === path.id ? (
                          <div className="flex items-center gap-2 flex-1">
                            <input
                              type="text"
                              value={editPathValue}
                              onChange={(e) => setEditPathValue(e.target.value)}
                              className="input-dark flex-1 font-mono text-xs py-1"
                              autoFocus
                            />
                            <button onClick={() => updateAliasPath(path.id)} className="p-1 text-green-400 hover:bg-green-500/10 rounded">
                              <Save className="w-3 h-3" />
                            </button>
                            <button onClick={() => { setEditingPath(null); setEditPathValue(""); }} className="p-1 text-slate-400 hover:bg-slate-700 rounded">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <span className="font-mono text-slate-300 truncate flex-1">{path.path}</span>
                            <div className="flex gap-1">
                              <button
                                onClick={() => { setEditingPath(path.id); setEditPathValue(path.path); }}
                                className="p-1 text-slate-500 hover:text-cyan-400"
                              >
                                <Edit2 className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => deleteAliasPath(path.id)}
                                className="p-1 text-slate-500 hover:text-red-400"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                    {aliasPaths.filter(p => p.group_id === group.id).length === 0 && (
                      <p className="text-slate-500 text-xs text-center py-4">No paths in this group</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {pathGroups.length === 0 && (
              <div className="card-dark p-12 text-center">
                <Tags className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-400">No Path Groups</h3>
                <p className="text-slate-500 mt-2">Create a group to organize your alias paths</p>
                <button onClick={() => setShowPathGroupForm(true)} className="btn-primary mt-4">
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Group
                </button>
              </div>
            )}

            {/* Ungrouped Paths */}
            {aliasPaths.filter(p => !p.group_id).length > 0 && (
              <div className="card-dark p-5">
                <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                  <Route className="w-5 h-5 text-slate-400" />
                  Ungrouped Paths ({aliasPaths.filter(p => !p.group_id).length})
                </h3>
                {/* Bulk Actions Bar */}
                <div className="flex items-center gap-3 mb-3">
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-400 hover:text-white">
                    <input
                      type="checkbox"
                      checked={aliasPaths.filter(p => !p.group_id).length > 0 && selectedAliasPaths.size === aliasPaths.filter(p => !p.group_id).length}
                      onChange={toggleAllUngroupedPaths}
                      className="accent-cyan-500"
                    />
                    Select All
                  </label>
                  {selectedAliasPaths.size > 0 && (
                    <>
                      <span className="text-xs text-slate-500">{selectedAliasPaths.size} selected</span>
                      <button
                        onClick={bulkDeleteAliasPaths}
                        disabled={bulkDeleteLoading}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600/20 text-red-400 border border-red-500/30 rounded-lg hover:bg-red-600/30 text-xs font-medium disabled:opacity-50"
                        data-testid="bulk-delete-aliases-btn"
                      >
                        {bulkDeleteLoading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                        Delete Selected
                      </button>
                      <button
                        onClick={() => setSelectedAliasPaths(new Set())}
                        className="text-xs text-slate-500 hover:text-white"
                      >Clear</button>
                    </>
                  )}
                </div>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {aliasPaths.filter(p => !p.group_id).map(path => (
                    <div key={path.id} className={`flex items-center gap-2 p-3 rounded-lg transition-colors ${selectedAliasPaths.has(path.id) ? 'bg-cyan-500/10 border border-cyan-500/30' : 'bg-slate-800/50 border border-transparent'}`}>
                      <input
                        type="checkbox"
                        checked={selectedAliasPaths.has(path.id)}
                        onChange={() => toggleAliasPath(path.id)}
                        className="accent-cyan-500 shrink-0"
                      />
                      {editingPath === path.id ? (
                        <div className="flex items-center gap-2 flex-1">
                          <input
                            type="text"
                            value={editPathValue}
                            onChange={(e) => setEditPathValue(e.target.value)}
                            className="input-dark flex-1 font-mono text-sm"
                            autoFocus
                          />
                          <button onClick={() => updateAliasPath(path.id)} className="p-2 text-green-400 hover:bg-green-500/10 rounded">
                            <Save className="w-4 h-4" />
                          </button>
                          <button onClick={() => { setEditingPath(null); setEditPathValue(""); }} className="p-2 text-slate-400 hover:bg-slate-700 rounded">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <span className="font-mono text-sm text-slate-300 truncate">{path.path}</span>
                          <div className="flex items-center gap-2">
                            <select
                              onChange={(e) => e.target.value && movePathToGroup(path.id, e.target.value)}
                              className="bg-slate-700 border border-slate-600 rounded px-2 py-1 text-xs text-white"
                              defaultValue=""
                            >
                              <option value="">Move to group...</option>
                              {pathGroups.map(g => (
                                <option key={g.id} value={g.id}>{g.name}</option>
                              ))}
                            </select>
                            <button
                              onClick={() => { setEditingPath(path.id); setEditPathValue(path.path); }}
                              className="p-1.5 text-slate-400 hover:text-cyan-400"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteAliasPath(path.id)}
                              className="p-1.5 text-slate-400 hover:text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add Ungrouped Path */}
            <div className="card-dark p-5">
              <h3 className="font-semibold text-white mb-4">Add Ungrouped Path</h3>
              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder="/oauth2/v2.0/authorize..."
                  value={newAliasPath}
                  onChange={(e) => setNewAliasPath(e.target.value)}
                  className="input-dark flex-1 font-mono text-sm"
                  data-testid="new-alias-input"
                />
                <button onClick={() => addAliasPath(null)} disabled={!newAliasPath} className="btn-primary flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Add Path
                </button>
              </div>
            </div>

            {/* System Path Generator */}
            <div className="card-dark p-5">
              <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-400" />
                System Path Generator
              </h3>
              <p className="text-slate-500 text-xs mb-4">Generate realistic campaign-style URL paths with varied patterns (OAuth, Cloud, API, SSO, etc.)</p>
              <div className="flex items-center gap-3 mb-4">
                <label className="text-slate-400 text-sm whitespace-nowrap">How many:</label>
                <input
                  type="number"
                  min="1"
                  max="7000"
                  value={genCount}
                  onChange={(e) => setGenCount(Math.max(1, Math.min(7000, parseInt(e.target.value) || 1)))}
                  className="input-dark w-28 text-center font-mono"
                  data-testid="gen-count-input"
                />
                <button
                  onClick={generatePaths}
                  disabled={genLoading}
                  className="btn-primary flex items-center gap-2"
                  data-testid="generate-paths-btn"
                >
                  {genLoading ? (
                    <><RefreshCw className="w-4 h-4 animate-spin" />Generating...</>
                  ) : (
                    <><Zap className="w-4 h-4" />Generate Paths</>
                  )}
                </button>
              </div>

              {/* Generated paths preview */}
              {generatedPaths.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-slate-300">{selectedGenPaths.size} of {generatedPaths.length} selected</span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedGenPaths(new Set(generatedPaths.map((_, i) => i)))}
                        className="text-xs text-cyan-400 hover:text-cyan-300"
                      >Select All</button>
                      <button
                        onClick={() => setSelectedGenPaths(new Set())}
                        className="text-xs text-slate-400 hover:text-white"
                      >Deselect All</button>
                    </div>
                  </div>
                  {generatedPaths.length > 50 && (
                    <div className="text-xs text-amber-400 mb-2">Showing first 50 of {generatedPaths.length} generated paths. All selected paths will be added.</div>
                  )}
                  <div className="space-y-1.5 max-h-72 overflow-y-auto mb-4 pr-1">
                    {generatedPaths.slice(0, 50).map((path, idx) => (
                      <label
                        key={idx}
                        className={`flex items-start gap-2 p-2.5 rounded-lg cursor-pointer transition-colors ${
                          selectedGenPaths.has(idx)
                            ? "bg-cyan-500/10 border border-cyan-500/30"
                            : "bg-slate-800/50 border border-slate-700/50 hover:border-slate-600"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedGenPaths.has(idx)}
                          onChange={() => toggleGenPath(idx)}
                          className="mt-1 accent-cyan-500"
                        />
                        <span className="font-mono text-xs text-slate-300 break-all leading-relaxed">{path}</span>
                      </label>
                    ))}
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={addSelectedGenPaths}
                      disabled={selectedGenPaths.size === 0}
                      className="btn-primary flex items-center gap-2"
                      data-testid="add-generated-paths-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Add {selectedGenPaths.size} Path{selectedGenPaths.size !== 1 ? 's' : ''} to System
                    </button>
                    <button
                      onClick={() => { setGeneratedPaths([]); setSelectedGenPaths(new Set()); }}
                      className="btn-secondary flex items-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      Discard
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* User Assignment Modal */}
            {showUserAssignModal && (
              <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
                <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <UserCheck className="w-5 h-5 text-green-400" />
                    Assign Users to Path Group
                  </h3>
                  <p className="text-slate-400 text-sm mb-4">
                    Select licensed users who should receive paths from this group:
                  </p>
                  <div className="space-y-2 max-h-64 overflow-y-auto mb-4">
                    {licensedUsersForAssign.map(user => {
                      const alreadyAssigned = user.assigned_group_ids?.includes(assigningGroupId);
                      return (
                        <label
                          key={user.id}
                          className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                            alreadyAssigned 
                              ? "bg-green-500/10 border border-green-500/30" 
                              : selectedUsersForAssign.includes(user.id)
                                ? "bg-cyan-500/10 border border-cyan-500/30"
                                : "bg-slate-800 border border-slate-700 hover:border-slate-600"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={selectedUsersForAssign.includes(user.id) || alreadyAssigned}
                              disabled={alreadyAssigned}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedUsersForAssign([...selectedUsersForAssign, user.id]);
                                } else {
                                  setSelectedUsersForAssign(selectedUsersForAssign.filter(id => id !== user.id));
                                }
                              }}
                              className="w-4 h-4 rounded"
                            />
                            <div>
                              <p className="text-white font-medium">{user.username}</p>
                            </div>
                          </div>
                          {alreadyAssigned && (
                            <span className="text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded">
                              Already assigned
                            </span>
                          )}
                        </label>
                      );
                    })}
                    {licensedUsersForAssign.length === 0 && (
                      <p className="text-slate-500 text-center py-4">No licensed users available</p>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={assignUsersToGroup}
                      disabled={selectedUsersForAssign.length === 0}
                      className="btn-primary flex-1"
                    >
                      Assign {selectedUsersForAssign.length} User(s)
                    </button>
                    <button
                      onClick={() => { setShowUserAssignModal(false); setSelectedUsersForAssign([]); }}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* View Group Users Modal */}
            {showGroupUsersModal && (
              <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
                <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-cyan-400" />
                    Assigned Users
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto mb-4">
                    {groupUsers.map(assignment => (
                      <div key={assignment.id} className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                        <div>
                          <p className="text-white font-medium">{assignment.username}</p>
                        </div>
                        <button
                          onClick={() => unassignUserFromGroup(viewingGroupId, assignment.user_id)}
                          className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                          title="Remove user"
                        >
                          <UserX className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    {groupUsers.length === 0 && (
                      <p className="text-slate-500 text-center py-4">No users assigned to this group</p>
                    )}
                  </div>
                  <button
                    onClick={() => { setShowGroupUsersModal(false); setGroupUsers([]); }}
                    className="btn-secondary w-full"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === "transactions" && (
          <TransactionLedgerWorkspace
            filter={transactionFilter}
            onApprove={async (transaction) => {
              const amount = Number(transaction.amount) || 0;
              if (await showConfirm("Approve Transaction", `Approve and credit $${amount.toFixed(2)} to ${transaction.username || "user"}?`)) verifyTransaction(transaction.transaction_id, "approved");
            }}
            onFilter={setTransactionFilter}
            onReject={(transaction) => verifyTransaction(transaction.transaction_id, "rejected", "")}
            transactions={pendingTransactions}
            user={user}
            walletProps={{ wallets: walletSettings, onChange: (type, value) => setWalletSettings((current) => ({ ...current, [type]: value })), onSave: saveWalletSettings, saving: walletSaving, message: walletMessage }}
          />
        )}
        {false && activeTab === "transactions" && (
          <div className="admin-section" data-testid="support-tickets-section">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-orbitron font-bold text-white">Transactions</h1>
                <p className="text-slate-400 text-sm mt-1">Review and verify crypto payments</p>
              </div>
              <div className="flex gap-2">
                {[
                  { value: "pending", label: "Pending", color: "yellow" },
                  { value: "all", label: "All", color: "slate" }
                ].map(filter => (
                  <button
                    key={filter.value}
                    onClick={() => setTransactionFilter(filter.value)}
                    className={`px-4 py-2 rounded-lg text-sm transition-all ${
                      transactionFilter === filter.value
                        ? filter.color === "yellow"
                          ? "bg-yellow-500/10 text-yellow-400 border border-yellow-500/30"
                          : "bg-cyan-500/10 text-cyan-400 border border-cyan-500/30"
                        : "bg-slate-800/50 text-slate-400 border border-slate-700/50 hover:border-slate-600"
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>

            {user?.username === "oracledev" && (
              <WalletEditor
                wallets={walletSettings}
                onChange={(type, value) => setWalletSettings((current) => ({ ...current, [type]: value }))}
                onSave={saveWalletSettings}
                saving={walletSaving}
                message={walletMessage}
              />
            )}

            <div className="card-dark p-5">
              <div className="space-y-3">
                {pendingTransactions.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">
                    {transactionFilter === "pending" ? "No pending transactions" : "No transactions"}
                  </p>
                ) : (
                  pendingTransactions.map((tx) => (
                    <div
                      key={tx.id}
                      className={`p-4 rounded-lg border ${
                        tx.status === "pending" ? "bg-yellow-500/5 border-yellow-500/30" :
                        tx.status === "approved" ? "bg-green-500/5 border-green-500/30" :
                        "bg-red-500/5 border-red-500/30"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <div className={`p-2 rounded-lg ${tx.wallet_type === "btc" ? "bg-orange-500/10" : "bg-teal-500/10"}`}>
                              {tx.wallet_type === "btc" ? (
                                <Bitcoin className="w-5 h-5 text-orange-400" />
                              ) : (
                                <span className="text-teal-400 font-bold text-sm">₮</span>
                              )}
                            </div>
                            <div>
                              <span className="font-medium text-white">{tx.username}</span>
                              <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                                tx.status === "pending" ? "bg-yellow-500/10 text-yellow-400" :
                                tx.status === "approved" ? "bg-green-500/10 text-green-400" :
                                "bg-red-500/10 text-red-400"
                              }`}>
                                {tx.status}
                              </span>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-slate-500 text-xs">Amount</p>
                              <p className="text-green-400 font-semibold">${(tx.amount || 0).toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-slate-500 text-xs">Wallet</p>
                              <p className={tx.wallet_type === "btc" ? "text-orange-400" : "text-teal-400"}>{(tx.wallet_type || "btc").toUpperCase()}</p>
                            </div>
                            <div className="col-span-2">
                              <p className="text-slate-500 text-xs">Transaction ID</p>
                              <p className="font-mono text-xs text-white">{tx.transaction_id}</p>
                            </div>
                          </div>
                          <p className="text-slate-500 text-xs mt-2">Submitted: {new Date(tx.created_at).toLocaleString()}</p>
                          {tx.admin_note && (
                            <p className="text-xs text-slate-400 mt-2 p-2 bg-slate-800/50 rounded">Note: {tx.admin_note}</p>
                          )}
                        </div>
                        
                        {tx.status === "pending" && (
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={async () => {
                                const amount = Number(tx.amount) || 0;
                                if (await showConfirm("Approve Transaction", `Approve and credit $${amount.toFixed(2)} to ${tx.username || 'user'}?`)) {
                                  verifyTransaction(tx.transaction_id, "approved");
                                }
                              }}
                              className="flex items-center gap-2 px-4 py-2 bg-green-500/10 text-green-400 border border-green-500/30 rounded-lg hover:bg-green-500/20 transition-all text-sm"
                              data-testid={`approve-tx-${tx.id}`}
                            >
                              <CheckCircle className="w-4 h-4" />
                              Approve
                            </button>
                            <button
                              onClick={() => {
                                verifyTransaction(tx.transaction_id, "rejected", "");
                              }}
                              className="flex items-center gap-2 px-4 py-2 bg-red-500/10 text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/20 transition-all text-sm"
                              data-testid={`reject-tx-${tx.id}`}
                            >
                              <XCircle className="w-4 h-4" />
                              Reject
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Support Tickets Tab */}
        {activeTab === "support" && (
          <SupportTicketWorkspace
            onClose={closeTicket}
            onRespond={respondToTicket}
            response={ticketResponse}
            selectedId={selectedTicket}
            setResponse={setTicketResponse}
            setSelectedId={setSelectedTicket}
            tickets={tickets}
          />
        )}
        {false && activeTab === "support" && (
          <div className="admin-section" data-testid="broadcast-section">
            <div>
              <h1 className="text-2xl font-orbitron font-bold text-white">Support Tickets</h1>
              <p className="text-slate-400 text-sm mt-1">Manage customer support requests</p>
            </div>

            <div className="card-dark p-5">
              <div className="space-y-3">
                {tickets.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No support tickets</p>
                ) : (
                  tickets.map((ticket) => (
                    <div key={ticket.id} className={`p-4 rounded-lg border ${
                      ticket.status === 'resolved' ? 'bg-green-500/5 border-green-500/20' :
                      ticket.status === 'open' ? 'bg-yellow-500/5 border-yellow-500/20' :
                      'bg-slate-800/50 border-slate-700/50'
                    }`}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium text-white">{ticket.subject}</h4>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              ticket.status === 'resolved' ? 'bg-green-500/10 text-green-400' :
                              ticket.status === 'open' ? 'bg-yellow-500/10 text-yellow-400' :
                              'bg-slate-700 text-slate-400'
                            }`}>
                              {ticket.status}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500">From: {ticket.username} • {new Date(ticket.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                      <p className="text-sm text-slate-300 mb-3 p-3 bg-slate-800/50 rounded">{ticket.message}</p>
                      
                      {ticket.response ? (
                        <div className="p-3 bg-cyan-500/5 border border-cyan-500/20 rounded">
                          <p className="text-xs text-cyan-400 mb-1">Admin Response:</p>
                          <p className="text-sm text-slate-300">{ticket.response}</p>
                        </div>
                      ) : null}
                      
                      {/* Conversation Thread */}
                      {ticket.responses && ticket.responses.length > 0 && (
                        <div className="space-y-2 mb-3 max-h-60 overflow-y-auto">
                          {ticket.responses.map((response, idx) => (
                            <div 
                              key={idx} 
                              className={`p-3 rounded ${
                                response.admin_username 
                                  ? "bg-cyan-500/10 border border-cyan-500/20" 
                                  : "bg-slate-800/50"
                              }`}
                            >
                              <p className={`text-xs mb-1 ${response.admin_username ? "text-cyan-400" : "text-slate-500"}`}>
                                {response.admin_username ? `Admin (${response.admin_username})` : `User (${ticket.username})`} • {new Date(response.created_at).toLocaleString()}
                              </p>
                              <p className="text-sm text-slate-300">{response.message}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Reply Input (always show unless closed) */}
                      {ticket.status !== "closed" && (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="Type your response..."
                            value={selectedTicket === ticket.id ? ticketResponse : ""}
                            onChange={(e) => { setSelectedTicket(ticket.id); setTicketResponse(e.target.value); }}
                            onFocus={() => setSelectedTicket(ticket.id)}
                            className="input-dark flex-1 text-sm"
                          />
                          <button
                            onClick={() => respondToTicket(ticket.id)}
                            disabled={selectedTicket !== ticket.id || !ticketResponse}
                            className="btn-primary flex items-center gap-2 text-sm"
                          >
                            <Send className="w-4 h-4" />
                            Reply
                          </button>
                          <button
                            onClick={() => closeTicket(ticket.id)}
                            className="px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded text-sm transition-colors"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      
                      {ticket.status === "closed" && (
                        <p className="text-center text-slate-500 text-sm py-2 bg-slate-800/30 rounded">Ticket closed</p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Broadcast Tab */}
        {activeTab === "broadcast" && (
          <BroadcastWorkspace
            draft={newNotification}
            notifications={notifications}
            onChange={setNewNotification}
            onDelete={deleteNotification}
            onSend={sendNotification}
          />
        )}
        {false && activeTab === "broadcast" && (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-orbitron font-bold text-white">Broadcast</h1>
              <p className="text-slate-400 text-sm mt-1">Send notifications to all users</p>
            </div>

            {/* Send Notification */}
            <div className="card-dark p-5">
              <h3 className="font-semibold text-white mb-4">New Notification</h3>
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Notification title"
                  value={newNotification.title}
                  onChange={(e) => setNewNotification({ ...newNotification, title: e.target.value })}
                  className="input-dark w-full"
                />
                <textarea
                  placeholder="Notification message..."
                  value={newNotification.message}
                  onChange={(e) => setNewNotification({ ...newNotification, message: e.target.value })}
                  className="input-dark w-full h-24 resize-none"
                />
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    {["info", "warning", "success", "announcement"].map(type => (
                      <button
                        key={type}
                        onClick={() => setNewNotification({ ...newNotification, type })}
                        className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${
                          newNotification.type === type
                            ? type === "info" ? "bg-blue-500/10 text-blue-400 border-blue-500/30" :
                              type === "warning" ? "bg-yellow-500/10 text-yellow-400 border-yellow-500/30" :
                              type === "success" ? "bg-green-500/10 text-green-400 border-green-500/30" :
                              "bg-purple-500/10 text-purple-400 border-purple-500/30"
                            : "bg-slate-800/50 text-slate-400 border-slate-700/50"
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={sendNotification}
                    disabled={!newNotification.title || !newNotification.message}
                    className="btn-primary flex items-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    Send to All Users
                  </button>
                </div>
              </div>
            </div>

            {/* Sent Notifications */}
            <div className="card-dark p-5">
              <h3 className="font-semibold text-white mb-4">Sent Notifications ({notifications.length})</h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {notifications.map(notif => (
                  <div key={notif.id} className="flex items-start justify-between p-4 bg-slate-800/50 border border-slate-700/50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-white">{notif.title}</h4>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          notif.type === "info" ? "bg-blue-500/10 text-blue-400" :
                          notif.type === "warning" ? "bg-yellow-500/10 text-yellow-400" :
                          notif.type === "success" ? "bg-green-500/10 text-green-400" :
                          "bg-purple-500/10 text-purple-400"
                        }`}>
                          {notif.type}
                        </span>
                      </div>
                      <p className="text-sm text-slate-400">{notif.message}</p>
                      <p className="text-xs text-slate-600 mt-2">{new Date(notif.created_at).toLocaleString()}</p>
                    </div>
                    <button
                      onClick={() => deleteNotification(notif.id)}
                      className="p-2 text-slate-400 hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {notifications.length === 0 && (
                  <p className="text-slate-500 text-center py-8">No notifications sent yet</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Config Sync Tab */}
        {activeTab === "config-sync" && <div data-testid="config-sync-section"><ConfigSyncPanel token={token} /></div>}
        {/* SSL Management is now integrated into Deployment Wizard */}

        {/* Admin Security Tab */}
        {activeTab === "admin-security" && (
          <AdminSecurityWorkspace
            code={admin2FACode}
            enabled={admin2FAEnabled}
            error={admin2FAError}
            loading={admin2FALoading}
            onCodeChange={setAdmin2FACode}
            onDisable={disableAdmin2FA}
            onSetup={setupAdmin2FA}
            onVerify={verifyAdmin2FA}
            setupData={admin2FASetup}
          />
        )}
        {false && activeTab === "admin-security" && (
          <div className="admin-section" data-testid="admin-security-section">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Lock className="w-6 h-6 text-rose-400" />
                  Admin Security Settings
                </h2>
                <p className="text-slate-400 text-sm mt-1">Manage master password and admin access controls</p>
              </div>
            </div>

            {/* Master Password Section */}
            <div className="card-dark p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Key className="w-5 h-5 text-amber-400" />
                Global Master Password
              </h3>
              
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-4">
                <p className="text-amber-400 text-sm font-medium mb-2">⚠️ Security Notice</p>
                <p className="text-sm text-slate-300">
                  The master password allows super admins to login as ANY user without knowing their password or 2FA code.
                  Use this only for legitimate support purposes. All master logins are logged.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm text-slate-400 block mb-2">Current Master Password</label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 p-3 bg-slate-800 rounded-lg border border-slate-700 font-mono text-sm">
                      <span className="text-slate-500">••••••••••••••••••••</span>
                    </div>
                    <button className="p-3 bg-slate-800 hover:bg-slate-700 rounded-lg border border-slate-700 transition-colors">
                      <Eye className="w-4 h-4 text-slate-400" />
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Stored securely in server environment</p>
                </div>

                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <h4 className="font-medium text-white mb-3">How to Use</h4>
                  <ol className="text-sm text-slate-400 space-y-2 list-decimal list-inside">
                    <li>Go to the login page</li>
                    <li>Enter the <strong className="text-white">target user's username</strong></li>
                    <li>Enter the <strong className="text-amber-400">master password</strong> instead of their password</li>
                    <li>You will be logged in as that user (2FA is bypassed)</li>
                  </ol>
                </div>

                <div className="p-4 bg-rose-500/10 border border-rose-500/30 rounded-lg">
                  <h4 className="font-medium text-rose-400 mb-2">To Change Master Password</h4>
                  <p className="text-sm text-slate-300 mb-2">
                    Edit the <code className="bg-slate-800 px-1 rounded">MASTER_PASSWORD</code> variable in your server's <code className="bg-slate-800 px-1 rounded">.env</code> file:
                  </p>
                  <code className="block p-2 bg-slate-900 rounded text-xs text-green-400">
                    MASTER_PASSWORD=YourNewSecurePassword123!
                  </code>
                  <p className="text-xs text-slate-500 mt-2">Then restart the backend service</p>
                </div>
              </div>
            </div>

            {/* Login Activity Log Placeholder */}
            <div className="card-dark p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-cyan-400" />
                Master Login Activity
              </h3>
              
              <div className="text-center py-8 text-slate-500">
                <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No master logins recorded yet</p>
                <p className="text-xs mt-1">Master logins will be logged here for security auditing</p>
              </div>
            </div>

            {/* Two-Factor Authentication */}
            <div className="card-dark p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Lock className="w-5 h-5 text-amber-400" />
                Two-Factor Authentication (2FA)
              </h3>
              
              {admin2FAError && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {admin2FAError}
                </div>
              )}

              {admin2FAEnabled ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <Shield className="w-6 h-6 text-green-400" />
                    <span className="text-green-400 font-medium">2FA is enabled - Your account is protected</span>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm text-slate-400 block">Enter current 2FA code to disable</label>
                    <div className="flex gap-3">
                      <input
                        type="text"
                        value={admin2FACode}
                        onChange={(e) => setAdmin2FACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        className="input-dark w-32 text-center tracking-widest font-mono"
                        maxLength={6}
                      />
                      <button
                        onClick={disableAdmin2FA}
                        disabled={admin2FALoading || admin2FACode.length !== 6}
                        className="px-4 py-2 bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/30 transition-colors disabled:opacity-50"
                      >
                        {admin2FALoading ? "Processing..." : "Disable 2FA"}
                      </button>
                    </div>
                  </div>
                </div>
              ) : admin2FASetup ? (
                <div className="space-y-4">
                  <p className="text-slate-400">Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)</p>
                  
                  <div className="flex justify-center">
                    <div className="p-4 bg-white rounded-lg">
                      <img src={admin2FASetup.qr_code} alt="2FA QR Code" className="w-48 h-48" />
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <p className="text-xs text-slate-500 mb-2">Or enter this code manually:</p>
                    <code className="text-cyan-400 bg-slate-800 px-3 py-1 rounded font-mono text-sm">{admin2FASetup.secret}</code>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm text-slate-400 block">Enter the 6-digit code from your app</label>
                    <div className="flex gap-3 justify-center">
                      <input
                        type="text"
                        value={admin2FACode}
                        onChange={(e) => setAdmin2FACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        className="input-dark w-32 text-center tracking-widest font-mono"
                        maxLength={6}
                      />
                      <button
                        onClick={verifyAdmin2FA}
                        disabled={admin2FALoading || admin2FACode.length !== 6}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
                      >
                        {admin2FALoading ? "Verifying..." : "Enable 2FA"}
                      </button>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => { setAdmin2FASetup(null); setAdmin2FACode(""); setAdmin2FAError(""); }}
                    className="text-slate-400 hover:text-white text-sm"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <AlertTriangle className="w-6 h-6 text-amber-400" />
                    <span className="text-amber-400">2FA is not enabled - Enable it for better security</span>
                  </div>
                  
                  <button
                    onClick={setupAdmin2FA}
                    disabled={admin2FALoading}
                    className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors disabled:opacity-50"
                  >
                    {admin2FALoading ? "Setting up..." : "Setup 2FA"}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        {rebalanceModal.open && (
          <div className="fixed inset-0 z-[998] grid place-items-center bg-black/80 p-4" data-testid="rebalance-dns-modal">
            <div className="w-full max-w-2xl rounded-md border border-slate-700 bg-slate-900 p-6 shadow-2xl">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-white" data-testid="rebalance-dns-title">Rebalance DNS helper</h3>
                  <p className="mt-1 text-sm text-slate-400">Move one dashboard assignment, then update its A record with the exact value shown.</p>
                </div>
                <button type="button" onClick={() => setRebalanceModal((current) => ({ ...current, open: false }))} data-testid="rebalance-dns-close-btn" className="rounded p-2 text-slate-400 hover:bg-slate-800 hover:text-white">✕</button>
              </div>

              {rebalanceLoading && <div className="mt-6 flex items-center gap-2 text-cyan-300" data-testid="rebalance-dns-loading"><RefreshCw className="h-4 w-4 animate-spin" /> Preparing safe target…</div>}
              {!rebalanceLoading && rebalanceModal.plan && !rebalanceModal.result && (
                <div className="mt-6 space-y-5">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <label className="block">
                      <span className="mb-2 block text-sm text-slate-400">Domain</span>
                      <select value={rebalanceModal.domainId} onChange={(event) => setRebalanceModal((current) => ({ ...current, domainId: event.target.value }))} data-testid="rebalance-domain-select" className="input-dark w-full">
                        {rebalanceModal.plan.recommendations.map((item) => <option key={item.domain_id} value={item.domain_id}>{item.domain}</option>)}
                      </select>
                    </label>
                    <label className="block">
                      <span className="mb-2 block text-sm text-slate-400">Target agent</span>
                      <select value={rebalanceModal.targetId} onChange={(event) => setRebalanceModal((current) => ({ ...current, targetId: event.target.value }))} data-testid="rebalance-target-select" className="input-dark w-full">
                        {rebalanceModal.plan.candidates.map((item) => <option key={item.id} value={item.id}>{item.name} · {item.ip_address} · {item.domain_count} domains</option>)}
                      </select>
                    </label>
                  </div>
                  {!rebalanceModal.plan.recommendations.length && <p className="rounded-md border border-slate-700 p-4 text-sm text-slate-400" data-testid="rebalance-no-domains">This server has no domains to move.</p>}
                  <div className="flex justify-end">
                    <button type="button" onClick={executeRebalance} disabled={!rebalanceModal.domainId || !rebalanceModal.targetId || rebalanceLoading} data-testid="rebalance-confirm-btn" className="rounded-md bg-violet-500 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-400 disabled:opacity-40">Move assignment</button>
                  </div>
                </div>
              )}

              {rebalanceModal.result && (
                <div className="mt-6 space-y-4" data-testid="rebalance-dns-result">
                  <div className="rounded-md border border-green-500/30 bg-green-500/10 p-4 text-sm text-green-200" data-testid="rebalance-sync-success">Agent sync succeeded. Fingerproxy certificate issuance was queued automatically; DNS still needs the following change.</div>
                  <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 rounded-md border border-slate-700 bg-slate-950 p-4 font-mono text-sm">
                    <span className="text-slate-500">Type</span><span data-testid="rebalance-dns-record-type">{rebalanceModal.result.dns_change.record_type}</span>
                    <span className="text-slate-500">Name</span><span className="break-all" data-testid="rebalance-dns-record-name">{rebalanceModal.result.dns_change.name}</span>
                    <span className="text-slate-500">Old</span><span data-testid="rebalance-dns-old-value">{rebalanceModal.result.dns_change.old_value}</span>
                    <span className="text-slate-500">New</span><span className="text-cyan-300" data-testid="rebalance-dns-new-value">{rebalanceModal.result.dns_change.new_value}</span>
                  </div>
                  <p className="text-xs text-cyan-200" data-testid="rebalance-fingerproxy-status">TLS status: {rebalanceModal.result.fingerproxy?.status || "queued"}</p>
                  <button type="button" onClick={() => navigator.clipboard.writeText(rebalanceModal.result.dns_change.new_value)} data-testid="rebalance-copy-ip-btn" className="rounded-md border border-cyan-500/40 px-3 py-2 text-sm text-cyan-300 hover:bg-cyan-500/10">Copy target IP</button>
                </div>
              )}
              {rebalanceModal.error && <p className="mt-5 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300" data-testid="rebalance-dns-error">{rebalanceModal.error}</p>}
            </div>
          </div>
        )}
        {/* Global Confirmation Modal (always available across all tabs) */}
        {confirmModal.show && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[999]">
            <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-white mb-2">{confirmModal.title}</h3>
              <p className="text-slate-400 mb-6">{confirmModal.message}</p>
              <div className="flex gap-3">
                <button
                  onClick={confirmModal.onCancel}
                  className="flex-1 py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  data-testid="confirm-modal-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmModal.onConfirm}
                  className="flex-1 py-2 px-4 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors"
                  data-testid="confirm-modal-confirm"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SuperAdmin;
